<template>
  <div class="backend-view super-admin">
    <h1 class="mb-20">Super Admin Panel</h1>

    <div class="tab-buttons">
      <button
        v-for="tab in tabList"
        :key="tab.key"
        class="tab-btn"
        :class="{ active: activeTab === tab.key }"
        @click="activeTab = tab.key"
      >{{ tab.label }}</button>
    </div>

      <!-- ═══ Tab 1: General ═══ -->
      <div v-show="activeTab === 'general'" class="tab-content">

          <md-card>
            <md-card-header>
              <div class="md-title">Checkout modes</div>
              <div class="md-subhead">Debug and Preview are mutually exclusive — at most one is active.</div>
            </md-card-header>
            <md-card-content>
              <div class="md-layout md-gutter" style="align-items:center;">
                <div class="md-layout-item md-size-50" style="display:flex;align-items:center;gap:10px;">
                  <md-switch v-model="debugMode.enabled" class="md-primary" :disabled="loading.debugMode || loading.previewMode" @change="saveDebugMode">
                    Nezasa DEBUG mode {{ debugMode.enabled ? '(ON)' : '(off)' }}
                  </md-switch>
                  <md-progress-spinner v-if="loading.debugMode" md-mode="indeterminate" :md-diameter="20" />
                  <span v-if="debugMode.saved" class="heartbeat-saved">Saved!</span>
                </div>
                <div class="md-layout-item md-size-50">
                  <p style="font-size:12px;color:#666;margin:0;">
                    Developer mode. When ON, every create/update/delete the checkout would send to Nezasa
                    is intercepted and shown in a debug modal — useful for QA, not for showing to clients.
                  </p>
                </div>
              </div>

              <div class="md-layout md-gutter" style="align-items:center; margin-top:14px;">
                <div class="md-layout-item md-size-50" style="display:flex;align-items:center;gap:10px;">
                  <md-switch v-model="previewMode.enabled" class="md-accent" :disabled="loading.debugMode || loading.previewMode" @change="savePreviewMode">
                    Preview mode {{ previewMode.enabled ? '(ON)' : '(off)' }}
                  </md-switch>
                  <md-progress-spinner v-if="loading.previewMode" md-mode="indeterminate" :md-diameter="20" />
                  <span v-if="previewMode.saved" class="heartbeat-saved">Saved!</span>
                </div>
                <div class="md-layout-item md-size-50">
                  <p style="font-size:12px;color:#666;margin:0;">
                    Client-facing soft mode. The checkout shows a discreet "Preview" banner. No real bookings
                    are sent to Nezasa, but no developer modals appear — clients see a clean walkthrough.
                  </p>
                </div>
              </div>

              <pre class="debug-output" style="margin-top:12px;">{{ debugOutput }}</pre>
            </md-card-content>
          </md-card>

          <md-card>
            <md-card-header>
              <div class="md-title">Stripe payment</div>
              <div class="md-subhead">Global switch for Stripe card payments. When off, the per-profile Stripe option shows as "coming soon".</div>
            </md-card-header>
            <md-card-content>
              <div class="md-layout md-gutter" style="align-items:center;">
                <div class="md-layout-item md-size-50" style="display:flex;align-items:center;gap:10px;">
                  <md-switch v-model="stripeEnabledGlobal.enabled" class="md-primary" :disabled="loading.stripeEnabledGlobal" @change="saveStripeEnabledGlobal">
                    Enable Stripe {{ stripeEnabledGlobal.enabled ? '(ON)' : '(off)' }}
                  </md-switch>
                  <md-progress-spinner v-if="loading.stripeEnabledGlobal" md-mode="indeterminate" :md-diameter="20" />
                  <span v-if="stripeEnabledGlobal.saved" class="heartbeat-saved">Saved!</span>
                </div>
                <div class="md-layout-item md-size-50">
                  <p style="font-size:12px;color:#666;margin:0;">
                    When ON, brands can enable Stripe card payment in their checkout profiles. When OFF, the per-profile
                    Stripe toggle is disabled and labelled "coming soon" — existing stored keys are kept, not deleted.
                  </p>
                </div>
              </div>
            </md-card-content>
          </md-card>

          <!-- Password prompt when enabling Nezasa DEBUG mode -->
          <section v-if="debugPwModal.visible" class="ndbg-overlay" @click.self="cancelDebugPwModal">
            <div class="ndbg-modal md-elevation-10">
              <h3 style="margin:0 0 8px 0;">Confirm DEBUG mode</h3>
              <p style="color:#666;font-size:13px;margin:0 0 16px 0;">
                Enabling Nezasa DEBUG mode requires your account password. While DEBUG is ON, the
                Secrets vault tab will not require a 2FA code.
              </p>
              <md-field>
                <label>Your password</label>
                <md-input
                  v-model="debugPwModal.password"
                  type="password"
                  autocomplete="current-password"
                  @keyup.enter="confirmDebugPwModal"
                  ref="debugPwInput"
                />
              </md-field>
              <p v-if="debugPwModal.error" style="color:#f44336;font-size:13px;margin:4px 0 0 0;">{{ debugPwModal.error }}</p>
              <div style="display:flex;justify-content:flex-end;gap:8px;margin-top:16px;">
                <md-button class="md-raised" @click="cancelDebugPwModal">Cancel</md-button>
                <md-button class="md-raised md-primary" :disabled="!debugPwModal.password || debugPwModal.submitting" @click="confirmDebugPwModal">
                  <md-progress-spinner v-if="debugPwModal.submitting" md-mode="indeterminate" :md-diameter="18" style="margin-right:6px;" />
                  Enable DEBUG mode
                </md-button>
              </div>
            </div>
          </section>

          <md-card class="mb-20">
            <md-card-header>
              <div class="md-title">Bulk Trip Re-Import</div>
              <div class="md-subhead">Re-import all trips whose start date is within the selected range</div>
            </md-card-header>
            <md-card-content>
              <div class="md-layout md-gutter mb-20">
                <div class="md-layout-item md-size-30">
                  <md-field>
                    <label>Start date</label>
                    <md-input v-model="range.start" type="date" placeholder="" />
                  </md-field>
                </div>
                <div class="md-layout-item md-size-30">
                  <md-field>
                    <label>End date</label>
                    <md-input v-model="range.end" type="date" placeholder="" />
                  </md-field>
                </div>
                <div class="md-layout-item md-size-40" style="display:flex;align-items:flex-end;gap:10px;">
                  <md-button class="md-raised" :disabled="loading.range || !canRunRange" @click="previewRange">
                    <md-icon>search</md-icon>
                    <span style="margin-left:8px;">Preview trips</span>
                  </md-button>
                  <md-button class="md-raised md-primary" :disabled="loading.range || !canConfirmRange" @click="reimportRange">
                    <md-icon>sync</md-icon>
                    <span style="margin-left:8px;">Confirm re-import</span>
                  </md-button>
                  <md-progress-spinner v-if="loading.range" md-mode="indeterminate" :md-diameter="24" />
                </div>
              </div>

              <div v-if="preview.total !== null" class="range-preview">
                <div class="mb-10">
                  <strong>Trips found:</strong> {{ preview.total }}
                </div>
                <md-table v-if="preview.trips.length" md-card>
                  <md-table-row>
                    <md-table-head v-for="col in previewColumns" :key="col.key" style="text-align: center;">
                      {{ col.label }}
                    </md-table-head>
                  </md-table-row>
                  <md-table-row v-for="t in preview.trips" :key="t.trip_id" style="text-align: center;">
                    <md-table-cell v-for="col in previewColumns" :key="col.key">
                      {{ t[col.key] }}
                    </md-table-cell>
                  </md-table-row>
                </md-table>
              </div>
            </md-card-content>
          </md-card>

          <md-card class="mb-20">
            <md-card-header>
              <div class="md-title">Backfill Booking State</div>
              <div class="md-subhead">Read booking state from trip JSON files and update the database for all trips where it is missing</div>
            </md-card-header>
            <md-card-content>
              <div style="display:flex;align-items:center;gap:10px;">
                <md-button class="md-raised md-primary" :disabled="loading.backfill" @click="runBackfillBookingState">
                  <md-icon>sync</md-icon>
                  <span style="margin-left:8px;">Run Backfill</span>
                </md-button>
                <md-progress-spinner v-if="loading.backfill" md-mode="indeterminate" :md-diameter="24" />
              </div>
              <div v-if="backfillResult" class="backfill-result mt-10">
                <strong>Result:</strong>
                Total: {{ backfillResult.total }} |
                Updated: {{ backfillResult.updated }} |
                Skipped: {{ backfillResult.skipped }} |
                Errors: {{ backfillResult.errors }}
              </div>
            </md-card-content>
          </md-card>

          <md-card class="mb-20">
            <md-card-header>
              <div class="md-title">Heartbeat Monitor</div>
              <div class="md-subhead">Ping an external uptime URL at a configurable interval to monitor server health</div>
            </md-card-header>
            <md-card-content>
              <div class="md-layout md-gutter" style="align-items:center;">
                <div class="md-layout-item md-size-40">
                  <md-field>
                    <label>Heartbeat URL</label>
                    <md-input v-model="heartbeat.url" type="url" placeholder="https://uptime.betterstack.com/api/v1/heartbeat/..." />
                  </md-field>
                </div>
                <div class="md-layout-item md-size-15">
                  <md-field>
                    <label>Interval (minutes)</label>
                    <md-input v-model.number="heartbeat.minutes" type="number" min="1" />
                  </md-field>
                </div>
                <div class="md-layout-item md-size-15" style="display:flex;align-items:center;gap:8px;">
                  <md-switch v-model="heartbeat.enabled" class="md-primary">
                    {{ heartbeat.enabled ? 'Enabled' : 'Disabled' }}
                  </md-switch>
                </div>
                <div class="md-layout-item md-size-30" style="display:flex;align-items:center;gap:10px;">
                  <md-button class="md-raised md-primary" :disabled="loading.heartbeat" @click="saveHeartbeat">
                    <md-icon>save</md-icon>
                    <span style="margin-left:8px;">Save</span>
                  </md-button>
                  <md-progress-spinner v-if="loading.heartbeat" md-mode="indeterminate" :md-diameter="24" />
                  <span v-if="heartbeat.saved" class="heartbeat-saved">Saved!</span>
                </div>
              </div>

              <div v-if="heartbeat.enabled" class="heartbeat-status mt-10">
                <div class="md-layout md-gutter" style="align-items:center;">
                  <div class="md-layout-item md-size-50">
                    <div class="heartbeat-info">
                      <div>
                        <strong>Last heartbeat:</strong>
                        <span :class="heartbeat.lastStatus === 'ok' ? 'status-ok' : heartbeat.lastStatus === 'failed' ? 'status-failed' : ''">
                          {{ heartbeatLastAtFormatted }}
                          <span v-if="heartbeat.lastStatus">({{ heartbeat.lastStatus }})</span>
                        </span>
                      </div>
                      <div>
                        <strong>Next heartbeat in:</strong>
                        <span class="heartbeat-countdown">{{ heartbeatCountdownDisplay }}</span>
                      </div>
                    </div>
                  </div>
                  <div class="md-layout-item md-size-30" style="display:flex;align-items:center;gap:10px;">
                    <md-button class="md-raised" :disabled="loading.heartbeatPing" @click="manualHeartbeat">
                      <md-icon>favorite</md-icon>
                      <span style="margin-left:8px;">Send Heartbeat Now</span>
                    </md-button>
                    <md-progress-spinner v-if="loading.heartbeatPing" md-mode="indeterminate" :md-diameter="24" />
                  </div>
                </div>
              </div>
            </md-card-content>
          </md-card>

        </div>

      <!-- ═══ Tab 2: Webhooks ═══ -->
      <div v-show="activeTab === 'webhooks'" class="tab-content">

          <!-- Missing secret warning -->
          <div v-if="!webhookConfig.hasSecret" class="wh-missing-secret mb-20">
            <md-icon style="color:#f44336;margin-right:8px;">warning</md-icon>
            <span>Please add <code>NEZASA_WEBHOOK_SECRET</code> to environment variables and restart the server.</span>
          </div>

          <md-card class="mb-20">
            <md-card-header>
              <div class="md-title">Nezasa Webhooks</div>
              <div class="md-subhead">Manage Nezasa webhook subscriptions for a distribution channel</div>
            </md-card-header>
            <md-card-content>
              <!-- Channel ID + Load -->
              <div class="md-layout md-gutter" style="align-items:flex-end;">
                <div class="md-layout-item md-size-40">
                  <md-field :class="{ 'md-disabled': !webhookConfig.hasSecret }">
                    <label>Distribution Channel ID</label>
                    <md-input v-model="webhooks.channelId" type="text" placeholder="e.g. my-channel-id" :disabled="!webhookConfig.hasSecret" />
                  </md-field>
                </div>
                <div class="md-layout-item md-size-60" style="display:flex;align-items:center;gap:10px;">
                  <md-button class="md-raised md-primary" :disabled="!webhookConfig.hasSecret || loading.webhooks || !webhooks.channelId" @click="loadWebhooks">
                    <md-icon>refresh</md-icon>
                    <span style="margin-left:8px;">Load Webhooks</span>
                  </md-button>
                  <md-button class="md-raised" :disabled="!webhookConfig.hasSecret" @click="webhooks.showCreate = !webhooks.showCreate">
                    <md-icon>add</md-icon>
                    <span style="margin-left:8px;">{{ webhooks.showCreate ? 'Hide Create Form' : 'Create Webhook' }}</span>
                  </md-button>
                  <md-progress-spinner v-if="loading.webhooks" md-mode="indeterminate" :md-diameter="24" />
                </div>
              </div>

              <!-- Webhooks Table -->
              <div v-if="webhooks.list.length" class="mt-10">
                <md-table md-card>
                  <md-table-row>
                    <md-table-head>Name</md-table-head>
                    <md-table-head style="text-align:center;">Active</md-table-head>
                    <md-table-head>Target URL</md-table-head>
                    <md-table-head style="text-align:center;">Events</md-table-head>
                    <md-table-head style="text-align:center;">Actions</md-table-head>
                  </md-table-row>
                  <md-table-row v-for="wh in webhooks.list" :key="wh.id">
                    <md-table-cell>{{ wh.name }}</md-table-cell>
                    <md-table-cell style="text-align:center;">
                      <span :class="wh.active ? 'wh-badge-active' : 'wh-badge-inactive'">
                        {{ wh.active ? 'Active' : 'Inactive' }}
                      </span>
                    </md-table-cell>
                    <md-table-cell><span class="wh-url">{{ wh.target ? wh.target.url : wh.url }}</span></md-table-cell>
                    <md-table-cell style="text-align:center;">{{ wh.events ? wh.events.length : 0 }}</md-table-cell>
                    <md-table-cell style="text-align:center;">
                      <md-button v-if="isOurWebhook(wh)" class="md-icon-button" :disabled="loading.webhookPing || !wh.active" @click="pingWebhook(wh)">
                        <md-icon>cell_tower</md-icon>
                        <md-tooltip>Send test ping</md-tooltip>
                      </md-button>
                      <md-button class="md-icon-button" :disabled="loading.webhookUpdate" @click="editWebhook(wh)">
                        <md-icon>edit</md-icon>
                        <md-tooltip>Edit webhook</md-tooltip>
                      </md-button>
                      <md-button class="md-icon-button" :disabled="loading.webhookToggle" @click="toggleWebhookActive(wh)">
                        <md-icon>{{ wh.active ? 'pause' : 'play_arrow' }}</md-icon>
                        <md-tooltip>{{ wh.active ? 'Disable' : 'Enable' }}</md-tooltip>
                      </md-button>
                      <md-button class="md-icon-button" :disabled="loading.webhookDelete" @click="deleteWebhook(wh)">
                        <md-icon>delete</md-icon>
                        <md-tooltip>Delete webhook</md-tooltip>
                      </md-button>
                    </md-table-cell>
                  </md-table-row>
                </md-table>
              </div>
              <div v-else-if="webhooks.channelId && !loading.webhooks && webhooks.loaded" class="mt-10" style="color:#888;">
                No webhooks found for this channel.
              </div>

              <!-- Create Webhook Form -->
              <div v-if="webhooks.showCreate" class="wh-create-form mt-10">
                <h4 style="margin-bottom:10px;">Create Webhook</h4>
                <div class="md-layout md-gutter">
                  <div class="md-layout-item md-size-50">
                    <md-field>
                      <label>Name</label>
                      <md-input v-model="webhookForm.name" type="text" placeholder="My Webhook" />
                    </md-field>
                  </div>
                  <div class="md-layout-item md-size-50">
                    <md-field>
                      <label>Target URL</label>
                      <md-input v-model="webhookForm.url" type="url" placeholder="https://..." />
                    </md-field>
                  </div>
                </div>
                <div class="md-layout md-gutter">
                  <div class="md-layout-item md-size-50">
                    <md-field>
                      <label>Secret</label>
                      <md-input v-model="webhookForm.secret" type="text" />
                    </md-field>
                  </div>
                  <div class="md-layout-item md-size-25" style="display:flex;align-items:center;">
                    <md-switch v-model="webhookForm.reseller" class="md-primary">Reseller</md-switch>
                  </div>
                  <div class="md-layout-item md-size-25" style="display:flex;align-items:center;">
                    <md-switch v-model="webhookForm.active" class="md-primary">Active</md-switch>
                  </div>
                </div>
                <div class="wh-events-section mt-10">
                  <strong>Events:</strong>
                  <div class="wh-events-grid">
                    <label v-for="ev in availableWebhookEvents" :key="ev" class="wh-event-checkbox">
                      <input type="checkbox" :value="ev" v-model="webhookForm.events" />
                      {{ ev }}
                    </label>
                  </div>
                </div>
                <div style="display:flex;align-items:center;gap:10px;margin-top:12px;">
                  <md-button class="md-raised md-primary" :disabled="loading.webhookCreate || !webhookForm.name || !webhookForm.url" @click="createWebhook">
                    <md-icon>save</md-icon>
                    <span style="margin-left:8px;">Create</span>
                  </md-button>
                  <md-progress-spinner v-if="loading.webhookCreate" md-mode="indeterminate" :md-diameter="24" />
                </div>
              </div>

              <!-- Edit Webhook Form -->
              <div v-if="webhooks.showEdit && webhooks.editingWebhook" class="wh-create-form mt-10">
                <div style="display:flex;align-items:center;justify-content:space-between;">
                  <h4 style="margin-bottom:10px;">Edit Webhook — {{ webhooks.editingWebhook.id }}</h4>
                  <md-button class="md-icon-button" @click="cancelEditWebhook">
                    <md-icon>close</md-icon>
                    <md-tooltip>Cancel editing</md-tooltip>
                  </md-button>
                </div>
                <div class="md-layout md-gutter">
                  <div class="md-layout-item md-size-50">
                    <md-field>
                      <label>Name</label>
                      <md-input v-model="webhookEditForm.name" type="text" placeholder="My Webhook" />
                    </md-field>
                  </div>
                  <div class="md-layout-item md-size-50">
                    <md-field>
                      <label>Target URL</label>
                      <md-input v-model="webhookEditForm.url" type="url" placeholder="https://..." />
                    </md-field>
                  </div>
                </div>
                <div class="md-layout md-gutter">
                  <div class="md-layout-item md-size-50" style="display:flex;align-items:center;">
                    <md-switch v-model="webhookEditForm.reseller" class="md-primary">Reseller</md-switch>
                  </div>
                  <div class="md-layout-item md-size-50" style="display:flex;align-items:center;">
                    <md-switch v-model="webhookEditForm.active" class="md-primary">Active</md-switch>
                  </div>
                </div>
                <div class="wh-events-section mt-10">
                  <strong>Events:</strong>
                  <div class="wh-events-grid">
                    <label v-for="ev in availableWebhookEvents" :key="ev" class="wh-event-checkbox">
                      <input type="checkbox" :value="ev" v-model="webhookEditForm.events" />
                      {{ ev }}
                    </label>
                  </div>
                </div>
                <div style="display:flex;align-items:center;gap:10px;margin-top:12px;">
                  <md-button class="md-raised md-primary" :disabled="loading.webhookUpdate || !webhookEditForm.name || !webhookEditForm.url" @click="updateWebhook">
                    <md-icon>save</md-icon>
                    <span style="margin-left:8px;">Update</span>
                  </md-button>
                  <md-button class="md-raised" @click="cancelEditWebhook">
                    Cancel
                  </md-button>
                  <md-progress-spinner v-if="loading.webhookUpdate" md-mode="indeterminate" :md-diameter="24" />
                </div>
              </div>

              <!-- Webhook Console Log -->
              <div class="mt-10" style="display:flex;align-items:center;justify-content:space-between;">
                <strong>Console Log</strong>
                <md-button class="md-dense" @click="webhookLog = ''">Clear Log</md-button>
              </div>
              <pre class="debug-output wh-console">{{ webhookLog || 'No log entries yet.' }}</pre>
            </md-card-content>
          </md-card>

          <!-- Implemented Webhook Routes -->
          <md-card class="mb-20">
            <md-card-header>
              <div class="md-title">Implemented Webhook Routes</div>
              <div class="md-subhead">Webhook endpoint: <code class="wh-event-code">POST {{ webhookEndpointUrl }}</code></div>
            </md-card-header>
            <md-card-content>
              <md-table md-card>
                <md-table-row>
                  <md-table-head>Event Type</md-table-head>
                  <md-table-head>Action</md-table-head>
                  <md-table-head style="text-align:center;">Sets booking_state</md-table-head>
                </md-table-row>
                <md-table-row v-for="route in implementedWebhookRoutes" :key="route.event">
                  <md-table-cell><code class="wh-event-code">{{ route.event }}</code></md-table-cell>
                  <md-table-cell>{{ route.action }}</md-table-cell>
                  <md-table-cell style="text-align:center;">
                    <code v-if="route.state" class="wh-state-badge">{{ route.state }}</code>
                    <span v-else style="color:#888;">—</span>
                  </md-table-cell>
                </md-table-row>
              </md-table>
            </md-card-content>
          </md-card>

        </div>

      <!-- ═══ Tab 3: Logs ═══ -->
      <div v-show="activeTab === 'logs'" class="tab-content">
          <md-card>
            <md-card-header>
              <div class="md-title">Server Logs</div>
              <div class="md-subhead">Browse server log files by category and date</div>
            </md-card-header>
            <md-card-content>
              <!-- Controls row -->
              <div class="logs-controls">
                <div class="logs-categories">
                  <md-button
                    v-for="cat in logCategories"
                    :key="cat.value"
                    :class="['md-raised', logs.category === cat.value ? 'md-primary' : '']"
                    @click="selectLogCategory(cat.value)"
                  >{{ cat.label }}</md-button>
                </div>
                <div class="logs-filters">
                  <select v-model="logs.selectedDate" class="logs-date-select" @change="loadLogEntries">
                    <option value="" disabled>Select date</option>
                    <option v-for="d in logs.dates" :key="d" :value="d">{{ d }}</option>
                  </select>
                  <input
                    v-model="logs.search"
                    type="text"
                    placeholder="Search logs..."
                    class="logs-search-input"
                    @keyup.enter="loadLogEntries"
                  />
                  <md-button class="md-raised md-primary" :disabled="loading.logsRead || !logs.selectedDate" @click="loadLogEntries">
                    <md-icon>refresh</md-icon>
                    <span style="margin-left:6px;">Load</span>
                  </md-button>
                  <md-progress-spinner v-if="loading.logsDates || loading.logsRead" md-mode="indeterminate" :md-diameter="24" />
                </div>
              </div>

              <!-- Stats bar + Export button -->
              <div v-if="logs.entries.length > 0" class="logs-toolbar mt-10">
                <div v-if="logs.search" class="logs-stats">
                  Showing {{ logs.entries.length }} of {{ logs.totalCount }} entries
                </div>
                <span v-if="logs.selectedEntries.length" style="font-size:13px;color:#666;">{{ logs.selectedEntries.length }} selected</span>
                <div style="flex:1;"></div>
                <md-button class="md-raised md-primary" :disabled="!logs.selectedEntries.length" @click="exportSelectedLogs">
                  <md-icon>file_download</md-icon>
                  <span style="margin-left:6px;">Export selected</span>
                </md-button>
              </div>

              <!-- Console terminal view -->
              <div v-if="logs.raw && logs.category === 'console'" class="mt-10">
                <div class="console-terminal">
                  <div class="console-terminal-header">
                    <span class="console-dot console-dot-red"></span>
                    <span class="console-dot console-dot-yellow"></span>
                    <span class="console-dot console-dot-green"></span>
                    <span class="console-terminal-title">Console Output — {{ logs.selectedDate }}</span>
                  </div>
                  <pre class="console-terminal-body" ref="consoleBody" v-html="ansiToHtml(logs.raw)"></pre>
                </div>
              </div>

              <!-- Log table -->
              <div v-else-if="logs.entries.length" class="mt-10">
                <md-table md-card>
                  <md-table-row>
                    <md-table-head style="width:40px;">
                      <md-checkbox v-model="logs.selectAll" @change="toggleSelectAllLogs" class="log-checkbox" />
                    </md-table-head>
                    <md-table-head style="width:160px;">Time</md-table-head>
                    <md-table-head style="width:100px;">Level</md-table-head>
                    <md-table-head style="width:200px;">Source</md-table-head>
                    <md-table-head>Message</md-table-head>
                  </md-table-row>
                  <template v-for="(entry, idx) in logs.entries">
                    <md-table-row :key="'log-' + idx" class="log-row log-row-clickable">
                      <md-table-cell @click.native.stop>
                        <md-checkbox v-model="logs.selectedEntries" :value="idx" class="log-checkbox" />
                      </md-table-cell>
                      <md-table-cell style="font-family:monospace;font-size:12px;" @click.native="toggleLogRow(idx)">{{ entry.timestamp }}</md-table-cell>
                      <md-table-cell @click.native="toggleLogRow(idx)">
                        <span :class="'log-level-badge log-level-' + entry.level.toLowerCase()">{{ entry.level }}</span>
                      </md-table-cell>
                      <md-table-cell style="font-family:monospace;font-size:12px;" @click.native="toggleLogRow(idx)">{{ entry.source }}</md-table-cell>
                      <md-table-cell @click.native="toggleLogRow(idx)" class="log-message-cell">
                        <span class="log-message-truncated">{{ truncateMessage(entry.message) }}</span>
                        <md-icon style="font-size:16px;margin-left:4px;vertical-align:middle;color:#888;">
                          {{ logs.expandedRow === idx ? 'expand_less' : 'expand_more' }}
                        </md-icon>
                      </md-table-cell>
                    </md-table-row>
                    <md-table-row v-if="logs.expandedRow === idx" :key="'stack-' + idx" class="log-stack-row">
                      <md-table-cell colspan="5">
                        <div class="log-detail-drawer">
                          <div v-if="entry.source || entry.event || entry.nezasa_id || entry.worker" class="log-detail-section log-detail-meta">
                            <span v-if="entry.source" class="log-meta-tag log-meta-source">{{ entry.source }}</span>
                            <span v-if="entry.event" class="log-meta-tag">Event: <strong>{{ entry.event }}</strong></span>
                            <span v-if="entry.nezasa_id" class="log-meta-tag">Nezasa ID: <strong>{{ entry.nezasa_id }}</strong></span>
                            <span v-if="entry.worker" class="log-meta-tag">Worker: <strong>{{ entry.worker }}</strong></span>
                          </div>
                          <div class="log-detail-section">
                            <div class="log-detail-label">Message</div>
                            <pre class="debug-output log-stack-pre">{{ entry.message }}</pre>
                          </div>
                          <div v-if="logExtraFields(entry)" class="log-detail-section">
                            <div class="log-detail-label">Details</div>
                            <pre class="debug-output log-stack-pre">{{ logExtraFields(entry) }}</pre>
                          </div>
                          <div v-if="entry.stack" class="log-detail-section">
                            <div class="log-detail-label">Stack Trace</div>
                            <pre class="debug-output log-stack-pre">{{ entry.stack }}</pre>
                          </div>
                        </div>
                      </md-table-cell>
                    </md-table-row>
                  </template>
                </md-table>
              </div>

              <!-- Empty state -->
              <div v-else-if="!loading.logsRead && logs.selectedDate && !logs.raw" class="mt-10" style="color:#888;text-align:left;padding:20px;">
                No log entries found.
              </div>
            </md-card-content>
          </md-card>
        </div>

      <!-- ═══ Tab 4: Database Operations ═══ -->
      <div v-show="activeTab === 'dbops'" class="tab-content">

          <md-card class="mb-20">
            <md-card-header>
              <div class="md-title">{{ options.Lang.ArchiveTrips || 'Archive Trips' }}</div>
              <div class="md-subhead">{{ options.Lang.ArchiveTripsSubhead || 'Archive all trips whose end date is before the selected date' }}</div>
            </md-card-header>
            <md-card-content>
              <div class="md-layout md-gutter" style="align-items:flex-end;">
                <div class="md-layout-item md-size-30">
                  <md-field class="md-focused">
                    <label>{{ options.Lang.ArchiveBeforeDate || 'Archive before date' }}</label>
                    <md-input v-model="archiveDate" type="date" :max="getTodayDate()" />
                  </md-field>
                </div>
                <div class="md-layout-item md-size-30">
                  <md-button
                    class="md-raised md-accent"
                    :disabled="!archiveDate || archiving"
                    @click="openDbOpsConfirm('archive-now')"
                  >
                    <span v-if="!archiving">{{ options.Lang.ArchiveNow || 'Archive now' }}</span>
                    <span v-else>{{ options.Lang.Archiving || 'Archiving...' }}</span>
                  </md-button>
                </div>
              </div>
            </md-card-content>
          </md-card>

          <md-card class="mb-20">
            <md-card-header>
              <div class="md-title">{{ options.Lang.DeleteTrips || 'Delete Trips' }}</div>
              <div class="md-subhead">{{ options.Lang.DeleteTripsSubhead || 'Soft-delete all trips whose end date is before the selected date' }}</div>
            </md-card-header>
            <md-card-content>
              <div class="md-layout md-gutter" style="align-items:flex-end;">
                <div class="md-layout-item md-size-30">
                  <md-field class="md-focused">
                    <label>{{ options.Lang.DeleteBeforeDate || 'Delete before date' }}</label>
                    <md-input v-model="deleteDate" type="date" :max="getTodayDate()" />
                  </md-field>
                </div>
                <div class="md-layout-item md-size-30">
                  <md-button
                    class="md-raised md-accent"
                    :disabled="!deleteDate || deleting"
                    @click="openDbOpsConfirm('delete-now')"
                  >
                    <span v-if="!deleting">{{ options.Lang.DeleteNow || 'Delete now' }}</span>
                    <span v-else>{{ options.Lang.Deleting || 'Deleting...' }}</span>
                  </md-button>
                </div>
              </div>
            </md-card-content>
          </md-card>

          <md-card class="mb-20">
            <md-card-header>
              <div class="md-title">{{ options.Lang.DeleteTripById || 'Delete Trip by ID' }}</div>
              <div class="md-subhead">{{ options.Lang.DeleteTripByIdSubhead || 'Permanently delete a single trip by its Trip ID (nezasa_id). This action is irreversible.' }}</div>
            </md-card-header>
            <md-card-content>
              <div class="md-layout md-gutter" style="align-items:flex-end;">
                <div class="md-layout-item md-size-30">
                  <md-field class="md-focused">
                    <label>{{ options.Lang.TripId || 'Trip ID (nezasa_id)' }}</label>
                    <md-input v-model="deleteTripId" type="text" />
                  </md-field>
                </div>
                <div class="md-layout-item md-size-30">
                  <md-button
                    class="md-raised md-accent"
                    :disabled="!deleteTripId || deleteTripIdLoading"
                    @click="confirmDeleteTripById"
                  >
                    <span v-if="!deleteTripIdLoading">{{ options.Lang.DeletePermanently || 'Delete permanently' }}</span>
                    <span v-else>{{ options.Lang.Deleting || 'Deleting...' }}</span>
                  </md-button>
                </div>
              </div>
            </md-card-content>
          </md-card>

          <md-card class="mb-20">
            <md-card-header>
              <div class="md-title">{{ options.Lang.AutomatedHandling || 'Automated Handling' }}</div>
              <div class="md-subhead">{{ options.Lang.AutomatedHandlingHint || 'Runs on the 1st of each month at 3 AM. Archive sets trips as done. Delete permanently removes trips, their PDFs and JSON files.' }}</div>
            </md-card-header>
            <md-card-content>
              <div class="md-layout md-gutter" style="align-items:flex-end;">
                <div class="md-layout-item md-size-25">
                  <md-field>
                    <label>{{ options.Lang.AutomatedHandlingMode || 'Mode' }}</label>
                    <md-select v-model="autoMode" @input="onAutoModeChange">
                      <md-option value="disabled">{{ options.Lang.Disabled || 'Disabled' }}</md-option>
                      <md-option value="archive">{{ options.Lang.AutomatedArchiveTrips || 'Auto archive' }}</md-option>
                      <md-option value="delete">{{ options.Lang.AutomatedDeleteTrips || 'Auto delete' }}</md-option>
                    </md-select>
                  </md-field>
                </div>
                <div class="md-layout-item md-size-25">
                  <md-field>
                    <label>{{ options.Lang.AutoPeriod || 'Period' }}</label>
                    <md-select v-model="autoPeriod">
                      <md-option value="1m">{{ options.Lang.Auto1Month || '1 Month before' }}</md-option>
                      <md-option value="3m">{{ options.Lang.Auto3Months || '3 Months before' }}</md-option>
                      <md-option value="1y">{{ options.Lang.Auto1Year || '1 Year before' }}</md-option>
                    </md-select>
                  </md-field>
                </div>
                <div class="md-layout-item md-size-20">
                  <md-button class="md-raised md-primary" @click="saveAutoSettings">
                    {{ options.Lang.Speichern || 'Save' }}
                  </md-button>
                </div>
                <div class="md-layout-item md-size-20">
                  <md-button class="md-raised md-accent" :disabled="!autoPeriod || autoMode !== 'delete' || testDeleteLoading" @click="testHardDelete">
                    <span v-if="!testDeleteLoading">Test Delete Now</span>
                    <span v-else>Loading...</span>
                  </md-button>
                </div>
              </div>

              <!-- Cron job status & countdown -->
              <div v-if="cronIsActive && dbOpsLoaded" class="cron-status-bar">
                <div class="cron-status-indicator">
                  <span class="cron-dot cron-dot-active"></span>
                  <span class="cron-label">{{ cronModeLabel }} &mdash; {{ options.Lang.CronActive || 'Active' }}</span>
                </div>
                <div class="cron-next-run">
                  <md-icon style="font-size:18px;margin-right:4px;">schedule</md-icon>
                  <span>{{ options.Lang.NextRun || 'Next run' }}: <strong>{{ cronNextRunFormatted }}</strong></span>
                  <span class="cron-countdown">({{ cronCountdownDisplay }})</span>
                </div>
              </div>

            </md-card-content>
          </md-card>

          <!-- Test Hard Delete Preview Overlay -->
          <section v-if="testDeleteDialog.visible" class="hd-overlay" @click.self="testDeleteDialog.visible = false">
            <div class="hd-modal md-elevation-10">
              <p class="hd-title">Permanent Delete Preview</p>
              <p class="hd-subtitle">Cutoff: <strong>{{ formatDateForDisplay(testDeleteDialog.cutoffDate) }}</strong> &mdash; {{ testDeleteDialog.trips.length }} trip(s) found</p>

              <!-- Warning -->
              <div class="hd-warning">
                <md-icon>warning</md-icon>
                <span>This action is <strong>irreversible</strong>. All listed trips, their DB records, JSON files and PDFs will be permanently deleted.</span>
              </div>

              <!-- Stats row -->
              <div class="hd-stats">
                <div class="hd-stat">
                  <span class="hd-stat-num">{{ testDeleteDialog.trips.length }}</span>
                  <span class="hd-stat-label">Trips</span>
                </div>
                <div class="hd-stat">
                  <span class="hd-stat-num">{{ testDeleteDialog.trips.filter(t => t.json_path).length }}</span>
                  <span class="hd-stat-label">JSON files</span>
                </div>
                <div class="hd-stat">
                  <span class="hd-stat-num">{{ testDeleteDialog.trips.reduce((s, t) => s + t.generated_pdfs.length, 0) }}</span>
                  <span class="hd-stat-label">Generated PDFs</span>
                </div>
                <div class="hd-stat">
                  <span class="hd-stat-num">{{ testDeleteDialog.trips.reduce((s, t) => s + t.uploaded_pdfs.length, 0) }}</span>
                  <span class="hd-stat-label">Uploaded PDFs</span>
                </div>
              </div>

              <!-- Table -->
              <div v-if="testDeleteDialog.trips.length > 0" class="hd-table-wrap">
                <table class="hd-table">
                  <thead>
                    <tr>
                      <th>Trip ID</th>
                      <th>End Date</th>
                      <th>JSON File</th>
                      <th>Generated PDFs</th>
                      <th>Uploaded PDFs</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="t in testDeleteDialog.trips" :key="t.trip_id">
                      <td class="hd-cell-id">{{ t.trip_id }}</td>
                      <td class="hd-cell-date">{{ formatDateForDisplay(t.date_end) || t.date_end }}</td>
                      <td>
                        <span v-if="t.json_path" class="hd-file-badge hd-file-json">{{ t.json_path.split('/').pop() }}</span>
                        <span v-else class="hd-none">-</span>
                      </td>
                      <td>
                        <template v-if="t.generated_pdfs.length > 0">
                          <span v-for="pdf in t.generated_pdfs" :key="pdf" class="hd-file-badge hd-file-pdf">{{ pdf }}</span>
                        </template>
                        <span v-else class="hd-none">-</span>
                      </td>
                      <td>
                        <template v-if="t.uploaded_pdfs.length > 0">
                          <span v-for="(p, pi) in t.uploaded_pdfs" :key="pi" class="hd-file-badge hd-file-upload">{{ p.pdfName || p.pdf }}</span>
                        </template>
                        <span v-else class="hd-none">-</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-else class="hd-empty">No trips found for the selected period.</div>

              <!-- Buttons -->
              <div class="hd-buttons">
                <md-button class="md-raised secondary" @click="testDeleteDialog.visible = false">
                  <strong>{{ options.Lang.schließen || 'Cancel' }}</strong>
                </md-button>
                <md-button
                  class="md-raised md-accent"
                  :disabled="testDeleteExecuting || testDeleteDialog.trips.length === 0"
                  @click="executeTestHardDelete"
                >
                  <strong v-if="!testDeleteExecuting">{{ options.Lang.löschen || 'Delete' }} ({{ testDeleteDialog.trips.length }})</strong>
                  <strong v-else>Deleting...</strong>
                </md-button>
              </div>
            </div>
          </section>

        </div>

      <!-- ═══ Tab 5: Files ═══ -->
      <div v-show="activeTab === 'files'" class="tab-content orphaned-files-tab">

        <md-card class="mb-20">
          <md-card-header class="files-card-header">
            <div class="md-title" style="text-align:center;width:100%;">{{ options.Lang.FilesOnDisk || 'Files on Disk' }}</div>
            <div class="md-subhead" style="text-align:left;max-width:600px;margin:8px auto 0;">{{ options.Lang.FilesOnDiskSubhead || 'All PDF and image files found on disk.' }} <span style="color:#f44336;font-weight:600;">{{ options.Lang.Orphaned || 'Orphaned' }}</span> {{ options.Lang.FilesNoMatchingTrip || 'files have no matching trip in the database.' }}</div>
          </md-card-header>
          <md-card-content>

            <div class="md-layout md-gutter" style="align-items:flex-end;">
              <div class="md-layout-item" style="flex:0 0 auto;">
                <md-button class="md-raised md-primary" :disabled="orphanedFiles.loading" @click="scanOrphanedFiles">
                  <span v-if="!orphanedFiles.loading">{{ options.Lang.ScanFiles || 'Scan files' }}</span>
                  <span v-else>{{ options.Lang.Scanning || 'Scanning...' }}</span>
                </md-button>
              </div>
              <div v-if="orphanedFiles.scanned" class="md-layout-item" style="flex:0 0 180px;">
                <md-field>
                  <label>{{ options.Lang.Folder || 'Folder' }}</label>
                  <md-select v-model="orphanedFiles.filterDir">
                    <md-option value="all">{{ options.Lang.AllFolders || 'All folders' }}</md-option>
                    <md-option v-for="dk in orphanedFiles.dirKeys" :key="dk" :value="dk">{{ dk }}</md-option>
                  </md-select>
                </md-field>
              </div>
              <div v-if="orphanedFiles.scanned" class="md-layout-item" style="flex:0 0 180px;">
                <md-field>
                  <label>{{ options.Lang.Status || 'Status' }}</label>
                  <md-select v-model="orphanedFiles.filterStatus">
                    <md-option value="all">{{ options.Lang.AllFiles || 'All files' }}</md-option>
                    <md-option value="orphaned">{{ options.Lang.OrphanedOnly || 'Orphaned only' }}</md-option>
                    <md-option value="linked">{{ options.Lang.LinkedOnly || 'Linked only' }}</md-option>
                  </md-select>
                </md-field>
              </div>
              <div v-if="orphanedFiles.scanned" class="md-layout-item" style="flex:1 1 200px;">
                <md-field>
                  <label>{{ options.Lang.SearchFilesPlaceholder || 'Search (trip ID, filename...)' }}</label>
                  <md-input v-model="orphanedFiles.search" type="text" />
                </md-field>
              </div>
            </div>

            <!-- Loading spinner -->
            <div v-if="orphanedFiles.loading" style="text-align:center;padding:20px;">
              <md-progress-spinner md-mode="indeterminate" :md-diameter="36" />
            </div>

            <!-- Results summary -->
            <div v-if="orphanedFiles.scanned && !orphanedFiles.loading" class="hd-stats" style="margin-top:16px;">
              <div class="hd-stat">
                <span class="hd-stat-num">{{ orphanedFiles.totalFiles }}</span>
                <span class="hd-stat-label">{{ options.Lang.TotalFiles || 'Total Files' }}</span>
              </div>
              <div class="hd-stat">
                <span class="hd-stat-num">{{ formatFileSize(orphanedFiles.totalSize) }}</span>
                <span class="hd-stat-label">{{ options.Lang.TotalSize || 'Total Size' }}</span>
              </div>
              <div class="hd-stat">
                <span class="hd-stat-num" style="color:#f44336;">{{ orphanedFiles.orphanedFileCount }}</span>
                <span class="hd-stat-label">{{ options.Lang.OrphanedFiles || 'Orphaned Files' }}</span>
              </div>
              <div class="hd-stat">
                <span class="hd-stat-num" style="color:#f44336;">{{ formatFileSize(orphanedFiles.orphanedSize) }}</span>
                <span class="hd-stat-label">{{ options.Lang.OrphanedSize || 'Orphaned Size' }}</span>
              </div>
            </div>

            <!-- Table -->
            <div v-if="filteredOrphanedRows.length > 0" style="margin-top:16px;">
              <div style="margin-bottom:8px;display:flex;align-items:center;gap:16px;">
                <md-checkbox v-model="orphanedFiles.selectAll" @change="toggleSelectAllOrphans">{{ options.Lang.SelectAllVisible || 'Select all visible' }}</md-checkbox>
                <span style="color:#999;font-size:13px;">{{ filteredOrphanedRows.length }} {{ options.Lang.FilesShown || 'file(s) shown' }}</span>
              </div>
              <div ref="orphanedTableScroll" class="orphaned-files-table-scroll">
                <table class="hd-table">
                  <thead>
                    <tr>
                      <th style="width:40px;"></th>
                      <th>{{ options.Lang.Status || 'Status' }}</th>
                      <th>{{ options.Lang.TripId || 'Trip / ID' }}</th>
                      <th>{{ options.Lang.Type || 'Type' }}</th>
                      <th>{{ options.Lang.Folder || 'Folder' }}</th>
                      <th>{{ options.Lang.Filename || 'Filename' }}</th>
                      <th>{{ options.Lang.Size || 'Size' }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in filteredOrphanedRows" :key="row.file.dirKey + '/' + (row.file.subDir || '') + '/' + row.file.filename" :class="{ 'orphaned-row': row.orphaned }">
                      <td><md-checkbox v-model="orphanedFiles.selected" :value="row.file" /></td>
                      <td>
                        <span v-if="row.orphaned" class="orphaned-badge">{{ options.Lang.Orphaned || 'Orphaned' }}</span>
                        <span v-else class="linked-badge">{{ options.Lang.Linked || 'Linked' }}</span>
                      </td>
                      <td class="hd-cell-id">{{ row.label }}</td>
                      <td><span class="type-badge">{{ row.type }}</span></td>
                      <td>{{ row.file.dirKey }}</td>
                      <td>{{ row.file.filename }}</td>
                      <td>{{ formatFileSize(row.file.sizeBytes) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div v-if="orphanedFiles.scanned && filteredOrphanedRows.length === 0 && !orphanedFiles.loading" class="hd-empty">
              {{ options.Lang.NoFilesMatchFilters || 'No files match the current filters.' }}
            </div>

            <!-- Delete buttons -->
            <div v-if="orphanedFiles.scanned && !orphanedFiles.loading" style="margin-top:12px;display:flex;gap:12px;align-items:center;">
              <md-button
                v-if="orphanedFiles.selected.length > 0"
                class="md-raised md-accent"
                :disabled="orphanedFiles.deleting"
                @click="deleteSelectedOrphans"
              >
                <span v-if="!orphanedFiles.deleting">{{ options.Lang.löschen || 'Delete' }} {{ orphanedFiles.selected.length }} {{ options.Lang.SelectedFiles || 'selected file(s)' }}</span>
                <span v-else>{{ options.Lang.Deleting || 'Deleting...' }}</span>
              </md-button>
              <md-button
                v-if="allOrphanedFiles.length > 0"
                class="md-raised md-accent"
                :disabled="orphanedFiles.deleting"
                @click="deleteAllOrphans"
              >
                <span v-if="!orphanedFiles.deleting">{{ options.Lang.DeleteAllOrphaned || 'Delete all' }} {{ allOrphanedFiles.length }} {{ options.Lang.OrphanedFilesLower || 'orphaned file(s)' }}</span>
                <span v-else>{{ options.Lang.Deleting || 'Deleting...' }}</span>
              </md-button>
            </div>

          </md-card-content>
        </md-card>

      </div>

      <!-- ═══ Tab 6: Secrets Vault ═══ -->
      <div v-show="activeTab === 'secrets'" class="tab-content">

        <!-- 2FA Gate -->
        <div v-if="!secrets.twoFaChecked" style="text-align:center;padding:40px;">
          <md-progress-spinner md-mode="indeterminate" :md-diameter="36" />
        </div>

        <div v-else-if="!secrets.debugBypass && !secrets.twoFaEnabled" class="secrets-2fa-gate">
          <md-card>
            <md-card-content style="text-align:center;padding:40px;">
              <md-icon class="md-size-3x" style="color:#f44336;">lock</md-icon>
              <h2 style="margin-top:16px;">2FA Required</h2>
              <p style="color:#666;margin-top:8px;">Please enable 2FA in your profile settings to access the Secrets tab.</p>
            </md-card-content>
          </md-card>
        </div>

        <!-- 2FA Verification Prompt -->
        <div v-else-if="!secrets.debugBypass && !secrets.twoFaVerified" class="secrets-2fa-gate">
          <md-card>
            <md-card-content style="text-align:center;padding:40px;max-width:400px;margin:0 auto;">
              <md-icon class="md-size-3x" style="color:#ff9800;">verified_user</md-icon>
              <h2 style="margin-top:16px;">Verify Your Identity</h2>
              <p style="color:#666;margin-top:8px;">Enter your 2FA code to access the Secrets vault.</p>
              <md-field style="margin-top:20px;">
                <label>6-digit TOTP Code</label>
                <md-input
                  v-model="secrets.totpCode"
                  type="text"
                  maxlength="6"
                  autocomplete="one-time-code"
                  style="text-align:center;font-size:24px;letter-spacing:8px;font-family:monospace;"
                  @keyup.enter="verifySecrets2FA"
                />
              </md-field>
              <p v-if="secrets.totpError" style="color:#f44336;font-size:13px;margin-top:4px;">{{ secrets.totpError }}</p>
              <md-button
                class="md-raised md-primary"
                style="margin-top:16px;"
                :disabled="!secrets.totpCode || secrets.totpCode.length < 6 || loading.secrets2fa"
                @click="verifySecrets2FA"
              >
                <md-progress-spinner v-if="loading.secrets2fa" md-mode="indeterminate" :md-diameter="20" style="margin-right:8px;" />
                <span>Verify</span>
              </md-button>
            </md-card-content>
          </md-card>
        </div>

        <template v-else>

          <!-- All Secrets Table -->
          <md-card class="mb-20">
            <md-card-header>
              <div class="md-title">All Secrets</div>
              <div class="md-subhead">Encrypted environment variables stored in the database</div>
            </md-card-header>
            <md-card-content>
              <div v-if="loading.secrets" style="text-align:center;padding:20px;">
                <md-progress-spinner md-mode="indeterminate" :md-diameter="28" />
              </div>
              <template v-else>
                <!-- Toolbar: filter + action buttons -->
                <div class="secrets-toolbar">
                  <div class="secrets-toolbar-left">
                    <md-field>
                      <label>Filter by Category</label>
                      <md-select v-model="secrets.filterCategory">
                        <md-option value="">All</md-option>
                        <md-option value="auth">Auth</md-option>
                        <md-option value="mail">Mail</md-option>
                        <md-option value="nezasa">Nezasa</md-option>
                        <md-option value="nezasa_sets">Nezasa Sets</md-option>
                        <md-option value="external">External</md-option>
                        <md-option value="general">General</md-option>
                      </md-select>
                    </md-field>
                  </div>
                  <div class="secrets-toolbar-right">
                    <md-button class="md-raised md-accent" @click="openNezasaEnableModal">
                      <md-icon>swap_horiz</md-icon>
                      <span style="margin-left:6px;">Enable Nezasa Set</span>
                    </md-button>
                    <md-button class="md-raised md-primary" @click="openAddSecretModal">
                      <md-icon>add</md-icon>
                      <span style="margin-left:6px;">Add Secret</span>
                    </md-button>
                  </div>
                </div>

                <md-table md-card>
                  <md-table-row>
                    <md-table-head style="width:12%;">Key Name</md-table-head>
                    <md-table-head style="width:35%;">Value</md-table-head>
                    <md-table-head style="width:8%;">Category</md-table-head>
                    <md-table-head style="width:35%;">Description</md-table-head>
                    <md-table-head style="width:10%;text-align:center;">Actions</md-table-head>
                  </md-table-row>
                  <md-table-row v-for="secret in filteredSecrets" :key="secret.key_name">
                    <md-table-cell style="font-family:monospace;font-weight:600;">{{ secret.key_name }}</md-table-cell>
                    <md-table-cell>
                      <div v-if="secrets.editingKey === secret.key_name" class="secret-edit-row">
                        <md-field style="margin:0;padding:0;min-height:auto;">
                          <md-input v-model="secrets.editValue" :type="secrets.showEditValue ? 'text' : 'password'" style="font-family:monospace;" />
                        </md-field>
                        <md-button class="md-icon-button md-dense" @click="secrets.showEditValue = !secrets.showEditValue">
                          <md-icon>{{ secrets.showEditValue ? 'visibility_off' : 'visibility' }}</md-icon>
                        </md-button>
                        <md-button class="md-icon-button md-dense md-primary" :disabled="loading.secretSave" @click="saveEditedSecret(secret.key_name)">
                          <md-icon>check</md-icon>
                        </md-button>
                        <md-button class="md-icon-button md-dense" @click="cancelEdit()">
                          <md-icon>close</md-icon>
                        </md-button>
                      </div>
                      <div v-else style="display:flex;align-items:center;">
                        <span v-if="!secrets.revealedValues[secret.key_name]" class="secret-masked" style="font-family:monospace;">{{ secret.masked_value }}</span>
                        <span v-else class="secret-revealed" style="font-family:monospace;word-break:break-all;">{{ secrets.revealedValues[secret.key_name] }}</span>
                        <md-button class="md-icon-button md-dense" @click="revealSecret(secret.key_name)" :disabled="loading.secretReveal">
                          <md-icon>{{ secrets.revealedValues[secret.key_name] ? 'visibility_off' : 'visibility' }}</md-icon>
                        </md-button>
                      </div>
                    </md-table-cell>
                    <md-table-cell>
                      <span class="secret-category-badge" :class="'cat-' + secret.category">{{ secret.category }}</span>
                    </md-table-cell>
                    <md-table-cell>{{ secret.description || '-' }}</md-table-cell>
                    <md-table-cell style="text-align:center;white-space:nowrap;">
                      <md-button class="md-icon-button md-dense" @click="startEditSecret(secret.key_name)">
                        <md-icon>edit</md-icon>
                        <md-tooltip>Edit</md-tooltip>
                      </md-button>
                      <md-button class="md-icon-button md-dense" @click="clearSecret(secret.key_name, secret.category, secret.description)">
                        <md-icon>backspace</md-icon>
                        <md-tooltip>Clear value</md-tooltip>
                      </md-button>
                      <md-button class="md-icon-button md-dense md-accent" @click="deleteSecretConfirm(secret.key_name)">
                        <md-icon>delete</md-icon>
                        <md-tooltip>Delete</md-tooltip>
                      </md-button>
                    </md-table-cell>
                  </md-table-row>
                </md-table>
              </template>
            </md-card-content>
          </md-card>

        </template>
      </div>

  </div>
</template>

<script>
export default {
  name: 'SuperAdmin',
  props: {
    options: {
      type: Object,
      required: true
    }
  },
  data () {
    const today = new Date().toISOString().slice(0, 10);
    return {
      activeTab: 'general',
      loading: {
        range: false,
        backfill: false,
        heartbeat: false,
        heartbeatPing: false,
        webhooks: false,
        webhookCreate: false,
        webhookToggle: false,
        webhookDelete: false,
        webhookPing: false,
        webhookUpdate: false,
        logsDates: false,
        logsRead: false,
        secrets: false,
        secretSave: false,
        secretDelete: false,
        secretReveal: false,
        nezasaSets: false,
        secrets2fa: false,
        debugMode: false,
        previewMode: false,
        stripeEnabledGlobal: false
      },
      debugMode: {
        enabled: false,
        saved: false
      },
      stripeEnabledGlobal: {
        enabled: false,
        saved: false
      },
      debugPwModal: {
        visible: false,
        password: '',
        error: '',
        submitting: false
      },
      previewMode: {
        enabled: false,
        saved: false
      },
      heartbeat: {
        url: '',
        enabled: false,
        saved: false,
        minutes: 3,
        lastAt: null,
        lastStatus: null,
        lastCronTickAt: null,
        intervalMinutes: 3
      },
      heartbeatCountdown: null,
      heartbeatTimer: null,
      heartbeatPollTimer: null,
      backfillResult: null,
      range: {
        start: today,
        end: today
      },
      preview: {
        total: null,
        trips: []
      },
      // dynamic column config for preview table
      previewColumns: [
        { key: 'trip_id', label: 'Trip ID' },
        { key: 'created_at', label: 'Created at' },
        { key: 'date_start', label: 'Start date' },
        { key: 'date_end', label: 'End date' }
      ],
      debugOutput: '',
      webhooks: {
        channelId: '',
        list: [],
        showCreate: false,
        showEdit: false,
        editingWebhook: null,
        loaded: false
      },
      webhookForm: {
        name: '',
        url: 'https://preview-api-explorer.viavio.travel/webhook/nezasa',
        secret: '',
        active: true,
        reseller: true,
        events: [
          'ping',
          'booking_completed',
          'booking_change_completed',
          'booking_modified',
          'cancellation_completed',
          'booking_requested',
          'booking_change_requested',
          'booking_failed',
          'booking_change_booking_failed'
        ]
      },
      webhookEditForm: {
        name: '',
        url: '',
        active: true,
        reseller: true,
        events: []
      },
      webhookLog: '',
      logs: {
        category: 'error',
        dates: [],
        selectedDate: '',
        search: '',
        entries: [],
        raw: '',
        totalCount: 0,
        expandedRow: null,
        selectedEntries: [],
        selectAll: false
      },
      logCategories: [
        { value: 'info', label: 'Info' },
        { value: 'error', label: 'Error' },
        { value: 'auth', label: 'Auth' },
        { value: 'webhooks', label: 'Webhooks' },
        { value: 'console', label: 'Console' },
        { value: 'delete', label: 'Delete Reports' }
      ],
      webhookConfig: { hasSecret: false, hasCredentials: false, secret: '' },
      implementedWebhookRoutes: [
        { event: 'ping', action: 'Health check — no-op (ACK only)', state: '' },
        { event: 'booking_completed', action: 'Update trip booking state', state: 'BookingCompleted' },
        { event: 'booking_change_completed', action: 'Update trip booking state', state: 'BookingCompleted' },
        { event: 'booking_modified', action: 'Update trip booking state', state: 'BookingCompleted' },
        { event: 'cancellation_completed', action: 'Update trip booking state', state: 'Cancelled' },
        { event: 'booking_requested', action: 'Update trip booking state', state: 'BookingRequested' },
        { event: 'booking_change_requested', action: 'Update trip booking state', state: 'BookingRequested' },
        { event: 'booking_failed', action: 'Update trip booking state', state: 'BookingFailed' },
        { event: 'booking_change_booking_failed', action: 'Update trip booking state', state: 'BookingFailed' },
        { event: 'booking_change_initiated', action: 'Acknowledged — no action yet', state: '' },
        { event: 'booking_change_cancellation_failed', action: 'Acknowledged — no action yet', state: '' },
        { event: 'pax_changed', action: 'Acknowledged — no action yet', state: '' },
        { event: 'booking_sync', action: 'Fetch latest booking state from Nezasa and update trip', state: 'dynamic' },
        { event: 'payment_added', action: 'Acknowledged — no action yet', state: '' },
        { event: 'payment_closed', action: 'Acknowledged — no action yet', state: '' },
        { event: 'payment_deleted', action: 'Acknowledged — no action yet', state: '' },
        { event: 'refund_added', action: 'Acknowledged — no action yet', state: '' },
        { event: 'refund_closed', action: 'Acknowledged — no action yet', state: '' },
        { event: 'refund_deleted', action: 'Acknowledged — no action yet', state: '' }
      ],
      availableWebhookEvents: [
        'ping',
        'booking_requested',
        'booking_completed',
        'booking_failed',
        'booking_modified',
        'booking_change_initiated',
        'booking_change_requested',
        'booking_change_completed',
        'booking_change_booking_failed',
        'booking_change_cancellation_failed',
        'cancellation_completed',
        'pax_changed',
        'booking_sync',
        'payment_added',
        'payment_closed',
        'payment_deleted',
        'refund_added',
        'refund_closed',
        'refund_deleted'
      ],
      // Database Operations tab
      archiveDate: null,
      archiving: false,
      deleteDate: null,
      deleting: false,
      deleteTripId: '',
      deleteTripIdLoading: false,
      autoMode: 'disabled',
      autoPeriod: null,
      prevAutoMode: 'disabled',
      dbOpsLoaded: false,
      testDeleteLoading: false,
      testDeleteExecuting: false,
      testDeleteDialog: {
        visible: false,
        cutoffDate: '',
        trips: []
      },
      cronCountdownNow: new Date(),
      cronCountdownTimer: null,
      // Files tab
      orphanedFiles: {
        loading: false,
        scanned: false,
        deleting: false,
        items: [],
        selected: [],
        selectAll: false,
        totalSize: 0,
        totalFiles: 0,
        orphanedFileCount: 0,
        orphanedSize: 0,
        dirKeys: [],
        filterDir: 'all',
        filterStatus: 'all',
        search: ''
      },
      // Secrets Vault tab
      secrets: {
        loaded: false,
        twoFaEnabled: false,
        twoFaChecked: false,
        twoFaVerified: false,
        debugBypass: false,
        totpCode: '',
        totpError: '',
        list: [],
        revealedValues: {},
        editingKey: null,
        editValue: '',
        showEditValue: false,
        showNewValue: false,
        filterCategory: '',
        showAddModal: false,
        newSecret: { key_name: '', value: '', category: 'general', description: '' }
      },
      nezasaSets: {
        activeSet: '',
        sets: [],
        showPassword: false,
        newSet: { name: '', username: '', password: '' }
      }
    }
  },
  created () {
    if (!this.isSuperAdmin) {
      this.$router.push('/trips');
    }
    this.loadHeartbeat();
    this.loadHeartbeatStatus();
    this.startHeartbeatTimer();
    this.loadWebhookConfig();
    this.loadLogDates();
    this.loadDbOpsSettings();
    this.loadDebugMode();
    this.loadStripeEnabledGlobal();
    this.cronCountdownTimer = setInterval(() => {
      this.cronCountdownNow = new Date();
    }, 1000);
    this.heartbeatPollTimer = setInterval(() => {
      this.loadHeartbeatStatus();
    }, 30000);
    this._resizeOrphanedBound = () => { if (this.activeTab === 'files') this.resizeOrphanedTable(); };
    window.addEventListener('resize', this._resizeOrphanedBound);
  },
  beforeDestroy () {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
    }
    if (this.heartbeatPollTimer) {
      clearInterval(this.heartbeatPollTimer);
    }
    if (this.cronCountdownTimer) {
      clearInterval(this.cronCountdownTimer);
    }
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
    if (this._resizeOrphanedBound) window.removeEventListener('resize', this._resizeOrphanedBound);
  },
  watch: {
    'heartbeat.enabled' (val) {
      this.saveHeartbeat();
      if (val) {
        this.loadHeartbeatStatus();
      }
    },
    'orphanedFiles.filterDir' () { this.resizeOrphanedTable(); },
    'orphanedFiles.filterStatus' () { this.resizeOrphanedTable(); },
    'orphanedFiles.search' () { this.resizeOrphanedTable(); },
    activeTab (val) {
      if (val === 'secrets' && !this.secrets.twoFaChecked) {
        this.checkSecrets2FA();
      }
      if (val === 'files' && !this.orphanedFiles.scanned) {
        this.scanOrphanedFiles();
      }
      // Lock page scroll when files tab is active
      document.documentElement.style.overflow = val === 'files' ? 'hidden' : '';
      document.body.style.overflow = val === 'files' ? 'hidden' : '';
      if (val === 'files') this.resizeOrphanedTable();
    }
  },
  computed: {
    tabList () {
      return [
        { key: 'general', label: 'General' },
        { key: 'webhooks', label: 'Webhooks' },
        { key: 'logs', label: 'Logs' },
        { key: 'dbops', label: this.options.Lang.DatabaseOperations || 'Database Operations' },
        { key: 'files', label: this.options.Lang.Files || 'Files' },
        { key: 'secrets', label: 'Secrets' }
      ];
    },
    isSuperAdmin () {
      return this.options?.user?.supersuper === 1 || this.options?.user?.supersuper === true;
    },
    canRunRange () {
      return !!(this.range.start && this.range.end && this.isSuperAdmin);
    },
    canConfirmRange () {
      return this.canRunRange && this.preview.total !== null && this.preview.total > 0;
    },
    heartbeatLastAtFormatted () {
      if (!this.heartbeat.lastAt) return 'Never';
      const d = new Date(this.heartbeat.lastAt);
      return d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
    },
    heartbeatCountdownDisplay () {
      if (this.heartbeatCountdown === null || this.heartbeatCountdown < 0) return '--:--';
      const mins = Math.floor(this.heartbeatCountdown / 60);
      const secs = this.heartbeatCountdown % 60;
      return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    },
    webhookEndpointUrl () {
      const base = (this.options.apiUrl || '').replace(/\/+$/, '');
      return `${base}/webhook/nezasa`;
    },
    cronIsActive () {
      return this.autoMode && this.autoMode !== 'disabled';
    },
    cronNextRun () {
      // Cron runs on 1st of each month at 03:00 server time
      const now = new Date();
      let next = new Date(now.getFullYear(), now.getMonth(), 1, 3, 0, 0, 0);
      // If we are past the 1st 03:00 this month, jump to next month
      if (now >= next) {
        next = new Date(now.getFullYear(), now.getMonth() + 1, 1, 3, 0, 0, 0);
      }
      return next;
    },
    cronNextRunFormatted () {
      const d = this.cronNextRun;
      return d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    },
    cronCountdown () {
      // Use cronCountdownNow (updated every second) so the countdown is reactive
      const diff = this.cronNextRun - this.cronCountdownNow;
      if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      const totalSec = Math.floor(diff / 1000);
      const days = Math.floor(totalSec / 86400);
      const hours = Math.floor((totalSec % 86400) / 3600);
      const minutes = Math.floor((totalSec % 3600) / 60);
      const seconds = totalSec % 60;
      return { days, hours, minutes, seconds };
    },
    cronCountdownDisplay () {
      const c = this.cronCountdown;
      const pad = (n) => String(n).padStart(2, '0');
      if (c.days > 0) {
        return `${c.days}d ${pad(c.hours)}h ${pad(c.minutes)}m ${pad(c.seconds)}s`;
      }
      return `${pad(c.hours)}h ${pad(c.minutes)}m ${pad(c.seconds)}s`;
    },
    cronModeLabel () {
      if (this.autoMode === 'archive') return this.options.Lang.AutomatedArchiveTrips || 'Auto archive';
      if (this.autoMode === 'delete') return this.options.Lang.AutomatedDeleteTrips || 'Auto delete';
      return '';
    },
    filteredSecrets () {
      if (!this.secrets.filterCategory) return this.secrets.list;
      if (this.secrets.filterCategory === 'nezasa_sets') {
        return this.secrets.list.filter(s => s.key_name && s.key_name.includes('__'));
      }
      return this.secrets.list.filter(s => s.category === this.secrets.filterCategory);
    },
    filteredOrphanedRows () {
      const search = (this.orphanedFiles.search || '').toLowerCase().trim();
      const rows = [];
      for (const item of this.orphanedFiles.items) {
        for (const file of item.files) {
          if (this.orphanedFiles.filterDir !== 'all' && file.dirKey !== this.orphanedFiles.filterDir) continue;
          if (this.orphanedFiles.filterStatus === 'orphaned' && !file.orphaned) continue;
          if (this.orphanedFiles.filterStatus === 'linked' && file.orphaned) continue;
          const label = (item.label || item.tripId || '').toLowerCase();
          if (search && !label.includes(search) && !file.filename.toLowerCase().includes(search) && !file.dirKey.toLowerCase().includes(search)) continue;
          rows.push({ tripId: item.tripId, label: item.label, orphaned: file.orphaned, type: file.type, file });
        }
      }
      return rows;
    },
    allOrphanedFiles () {
      const files = [];
      for (const item of this.orphanedFiles.items) {
        for (const file of item.files) {
          if (file.orphaned) files.push({ file, tripId: item.tripId });
        }
      }
      return files;
    }
  },
  methods: {
    async previewRange () {
      if (!this.canRunRange) return;
      this.loading.range = true;
      this.debugOutput = '';
      this.preview = { total: null, trips: [] };
      const self = this;
      const payload = {
        data: {
          startDate: this.range.start,
          endDate: this.range.end
        }
      };
      this.doPost(payload, 'superadmin/re-import-range-preview', function (res) {
        const body = res && res.data ? res.data : { total: 0, trips: [] };
        self.preview.total = body.data.total;
        self.preview.trips = body.data.trips || [];
        self.loading.range = false;
      });
    },
    async runBackfillBookingState () {
      this.loading.backfill = true;
      this.backfillResult = null;
      const self = this;
      this.doPost({}, 'superadmin/backfill-booking-state', function (res) {
        if (res.status === 'error') {
          self.backfillResult = { total: '?', updated: '?', skipped: '?', errors: 'Request failed' };
        } else {
          const d = res.data || {};
          self.backfillResult = d.data || d;
        }
        self.loading.backfill = false;
      });
    },
    loadDebugMode () {
      const self = this;
      this.doPost({}, 'superadmin/debug-mode', function (res) {
        const d = res && res.data && res.data.data ? res.data.data : {};
        self.debugMode.enabled = !!d.nezasa_debug_mode;
        self.previewMode.enabled = !!d.preview_mode;
      });
    },
    // Mutual-exclusion guard: turning one toggle on instantly turns the other
    // off in the UI so the user sees the constraint before they hit Save.
    onDebugToggle () {
      if (this.debugMode.enabled) this.previewMode.enabled = false;
    },
    onPreviewToggle () {
      if (this.previewMode.enabled) this.debugMode.enabled = false;
    },
    // Mirror the new debug/preview state into the shared GlobalSettings so the
    // sidebar badge and any other consumers re-render immediately, without
    // waiting for the next getglobalinfo refresh.
    syncGlobalModeFlags (data) {
      if (!this.options) return;
      const next = Object.assign({}, this.options.GlobalSettings || {}, {
        nezasa_debug_mode: data.nezasa_debug_mode ? 1 : 0,
        preview_mode: data.preview_mode ? 1 : 0
      });
      this.$set(this.options, 'GlobalSettings', next);
    },
    saveDebugMode () {
      // Enabling requires the user's password — open the modal and let
      // confirmDebugPwModal() drive the actual save. Disabling needs no password.
      if (this.debugMode.enabled) {
        this.openDebugPwModal();
        return;
      }
      this.persistDebugMode(false, '');
    },
    openDebugPwModal () {
      this.debugPwModal.password = '';
      this.debugPwModal.error = '';
      this.debugPwModal.submitting = false;
      this.debugPwModal.visible = true;
      this.$nextTick(() => {
        const el = this.$refs.debugPwInput && this.$refs.debugPwInput.$el
          ? this.$refs.debugPwInput.$el.querySelector('input')
          : null;
        if (el) el.focus();
      });
    },
    cancelDebugPwModal () {
      this.debugPwModal.visible = false;
      this.debugPwModal.password = '';
      this.debugPwModal.error = '';
      // Revert the switch since the user backed out.
      this.debugMode.enabled = false;
    },
    confirmDebugPwModal () {
      if (!this.debugPwModal.password || this.debugPwModal.submitting) return;
      this.debugPwModal.submitting = true;
      this.debugPwModal.error = '';
      this.persistDebugMode(true, this.debugPwModal.password);
    },
    persistDebugMode (enabled, password) {
      const self = this;
      self.loading.debugMode = true;
      self.debugMode.saved = false;
      const payload = { data: { nezasa_debug_mode: !!enabled } };
      if (enabled) payload.data.password = password;
      this.doPost(payload, 'superadmin/debug-mode-save', function (res) {
        self.loading.debugMode = false;
        self.debugPwModal.submitting = false;
        const ok = res && res.data && res.data.status !== 'error';
        if (ok) {
          const d = res.data.data || {};
          self.debugMode.enabled = !!d.nezasa_debug_mode;
          self.previewMode.enabled = !!d.preview_mode;
          self.syncGlobalModeFlags(d);
          self.debugMode.saved = true;
          setTimeout(() => { self.debugMode.saved = false; }, 2000);
          self.debugPwModal.visible = false;
          self.debugPwModal.password = '';
          // Re-check Secrets 2FA gating so the gate (or bypass) reflects the new state.
          if (self.activeTab === 'secrets') {
            self.secrets.twoFaChecked = false;
            self.checkSecrets2FA();
          }
        } else if (enabled) {
          // Show error inside the modal and keep the switch ON until user cancels.
          // doPost surfaces backend `msg` at the top level (res.msg) on error
          // responses; res.data.msg also exists on success-style errors.
          const msg = (res && (res.msg || (res.data && res.data.msg))) || 'Could not enable DEBUG mode.';
          self.debugPwModal.error = msg;
        }
      });
    },
    savePreviewMode () {
      const self = this;
      self.loading.previewMode = true;
      self.previewMode.saved = false;
      this.doPost({ data: { preview_mode: !!self.previewMode.enabled } }, 'superadmin/preview-mode-save', function (res) {
        self.loading.previewMode = false;
        if (res && res.data && res.data.status !== 'error') {
          const d = res.data.data || {};
          self.previewMode.enabled = !!d.preview_mode;
          self.debugMode.enabled = !!d.nezasa_debug_mode;
          self.syncGlobalModeFlags(d);
          self.previewMode.saved = true;
          setTimeout(() => { self.previewMode.saved = false; }, 2000);
        }
      });
    },
    loadStripeEnabledGlobal () {
      const self = this;
      this.doPost({}, 'superadmin/stripe-enabled', function (res) {
        const d = res && res.data && res.data.data ? res.data.data : {};
        self.stripeEnabledGlobal.enabled = !!d.stripe_enabled_global;
        self.syncGlobalStripeFlag(d);
      });
    },
    syncStripeFlagToGlobal (enabled) {
      if (!this.options) return;
      const next = Object.assign({}, this.options.GlobalSettings || {}, {
        stripe_enabled_global: enabled ? 1 : 0
      });
      this.$set(this.options, 'GlobalSettings', next);
    },
    syncGlobalStripeFlag (d) {
      this.syncStripeFlagToGlobal(!!(d && d.stripe_enabled_global));
    },
    saveStripeEnabledGlobal () {
      const self = this;
      self.loading.stripeEnabledGlobal = true;
      self.stripeEnabledGlobal.saved = false;
      this.doPost({ data: { stripe_enabled_global: !!self.stripeEnabledGlobal.enabled } }, 'superadmin/stripe-enabled-save', function (res) {
        self.loading.stripeEnabledGlobal = false;
        if (res && res.data && res.data.status !== 'error') {
          const d = res.data.data || {};
          self.stripeEnabledGlobal.enabled = !!d.stripe_enabled_global;
          self.syncStripeFlagToGlobal(self.stripeEnabledGlobal.enabled);
          self.stripeEnabledGlobal.saved = true;
          setTimeout(() => { self.stripeEnabledGlobal.saved = false; }, 2000);
        }
      });
    },
    loadHeartbeat () {
      const self = this;
      this.doPost({}, 'superadmin/heartbeat-settings', function (res) {
        const d = res && res.data && res.data.data ? res.data.data : {};
        self.heartbeat.url = d.heartbeat_url || '';
        self.heartbeat.enabled = !!d.heartbeat_enabled;
        self.heartbeat.minutes = parseInt(d.heartbeat_minutes, 10) || 3;
      });
    },
    saveHeartbeat () {
      this.loading.heartbeat = true;
      this.heartbeat.saved = false;
      const self = this;
      const payload = {
        data: {
          heartbeat_url: this.heartbeat.url,
          heartbeat_enabled: this.heartbeat.enabled,
          heartbeat_minutes: this.heartbeat.minutes
        }
      };
      this.doPost(payload, 'superadmin/heartbeat-settings-save', function (res) {
        self.loading.heartbeat = false;
        self.heartbeat.saved = true;
        setTimeout(() => { self.heartbeat.saved = false; }, 3000);
        // Apply the fresh status returned by the server (includes new interval + immediate heartbeat result)
        const d = res && res.data && res.data.data ? res.data.data : {};
        if (d.lastHeartbeatAt !== undefined) {
          self.applyHeartbeatStatus(d);
        }
      });
    },
    loadHeartbeatStatus () {
      const self = this;
      this.doPost({}, 'superadmin/heartbeat-status', function (res) {
        const d = res && res.data && res.data.data ? res.data.data : {};
        self.applyHeartbeatStatus(d);
      });
    },
    applyHeartbeatStatus (d) {
      this.heartbeat.lastAt = d.lastHeartbeatAt || null;
      this.heartbeat.lastStatus = d.lastHeartbeatStatus || null;
      this.heartbeat.lastCronTickAt = d.lastCronTickAt || null;
      this.heartbeat.intervalMinutes = d.intervalMinutes || 3;
      this.startHeartbeatTimer();
    },
    startHeartbeatTimer () {
      if (this.heartbeatTimer) {
        clearInterval(this.heartbeatTimer);
      }
      this.recalcCountdown();
      this.heartbeatTimer = setInterval(() => {
        this.recalcCountdown();
      }, 1000);
    },
    recalcCountdown () {
      const interval = this.heartbeat.intervalMinutes || 3;
      const lastTick = this.heartbeat.lastCronTickAt || this.heartbeat.lastAt;
      if (!lastTick) {
        this.heartbeatCountdown = null;
        return;
      }
      const nextAt = new Date(lastTick).getTime() + (interval * 60 * 1000);
      const secsRemaining = Math.round((nextAt - Date.now()) / 1000);
      const prev = this.heartbeatCountdown;
      this.heartbeatCountdown = Math.max(secsRemaining, 0);
      if (secsRemaining <= 0 && !this._waitingForTick) {
        this._waitingForTick = true;
        this.pollUntilNewTick(lastTick);
      }
    },
    pollUntilNewTick (oldTickAt, attempt) {
      attempt = attempt || 0;
      if (attempt > 10) {
        this._waitingForTick = false;
        return;
      }
      const self = this;
      setTimeout(() => {
        self.doPost({}, 'superadmin/heartbeat-status', function (res) {
          const d = res && res.data && res.data.data ? res.data.data : {};
          const newTick = d.lastCronTickAt || d.lastHeartbeatAt;
          if (newTick && newTick !== oldTickAt) {
            self.applyHeartbeatStatus(d);
            self._waitingForTick = false;
          } else {
            self.pollUntilNewTick(oldTickAt, attempt + 1);
          }
        });
      }, 2000);
    },
    manualHeartbeat () {
      this.loading.heartbeatPing = true;
      const self = this;
      this.doPost({}, 'superadmin/heartbeat-ping', function (res) {
        self.loading.heartbeatPing = false;
        const d = res && res.data && res.data.data ? res.data.data : {};
        self.heartbeat.lastAt = d.lastHeartbeatAt || null;
        self.heartbeat.lastStatus = d.lastHeartbeatStatus || null;
      });
    },
    async reimportRange () {
      if (!this.canConfirmRange) return;
      this.loading.range = true;
      this.debugOutput = '';
      const self = this;
      const payload = {
        data: {
          startDate: this.range.start,
          endDate: this.range.end
        }
      };
      this.doPost(payload, 'superadmin/re-import-range', function (res) {
        self.debugOutput = JSON.stringify(res, null, 2);
        self.loading.range = false;
      });
    },

    // ── Nezasa Webhooks ──────────────────────────────────────────────
    loadWebhookConfig () {
      const self = this;
      this.doPost({}, 'superadmin/nezasa-webhooks-config', function (res) {
        const d = res && res.data && res.data.data ? res.data.data : {};
        self.webhookConfig.hasSecret = !!d.hasSecret;
        self.webhookConfig.hasCredentials = !!d.hasCredentials;
        self.webhookConfig.secret = d.secret || '';
        if (d.secret && !self.webhookForm.secret) {
          self.webhookForm.secret = d.secret;
        }
      });
    },
    whLog (msg) {
      const now = new Date();
      const ts = [now.getHours(), now.getMinutes(), now.getSeconds()]
        .map(n => String(n).padStart(2, '0')).join(':');
      this.webhookLog += `[${ts}] ${msg}\n`;
    },
    loadWebhooks (silent) {
      if (!this.webhooks.channelId) return;
      this.loading.webhooks = true;
      if (!silent) {
        this.webhookLog = '';
        this.whLog('Loading webhooks for channel "' + this.webhooks.channelId + '"...');
      }
      const self = this;
      const payload = { data: { channelId: this.webhooks.channelId } };
      this.doPost(payload, 'superadmin/nezasa-webhooks-list', function (res) {
        self.loading.webhooks = false;
        self.webhooks.loaded = true;
        if (res && res.status === 'ok') {
          const d = res.data && res.data.data !== undefined ? res.data.data : res.data;
          const list = Array.isArray(d) ? d : (d && d.webhooks ? d.webhooks : []);
          self.webhooks.list = list;
          if (!silent) {
            self.whLog('✓ Loaded ' + list.length + ' webhook(s).');
            if (list.length) {
              self.whLog('Response:\n' + JSON.stringify(list, null, 2));
            }
          }
        } else {
          self.webhooks.list = [];
          if (!silent) {
            const errMsg = res && res.msg ? res.msg : 'Unknown error';
            self.whLog('✗ Error loading webhooks: ' + errMsg);
            if (res && res.data) {
              self.whLog('Stacktrace:\n' + JSON.stringify(res.data, null, 2));
            }
          }
        }
      });
    },
    createWebhook () {
      if (!this.webhooks.channelId || !this.webhookForm.name || !this.webhookForm.url) return;
      this.loading.webhookCreate = true;
      this.webhookLog = '';
      this.whLog('Creating webhook "' + this.webhookForm.name + '"...');
      const self = this;
      const target = { url: this.webhookForm.url };
      if (this.webhookForm.secret) {
        target.secret = this.webhookForm.secret;
      }
      const webhook = {
        name: this.webhookForm.name,
        active: this.webhookForm.active,
        reseller: this.webhookForm.reseller,
        events: this.webhookForm.events,
        target: target
      };
      const payload = {
        data: {
          channelId: this.webhooks.channelId,
          webhook: webhook
        }
      };
      this.doPost(payload, 'superadmin/nezasa-webhooks-create', function (res) {
        self.loading.webhookCreate = false;
        if (res && res.status === 'ok') {
          const d = res.data && res.data.data !== undefined ? res.data.data : res.data;
          const id = d && d.id ? d.id : '(unknown)';
          self.whLog('✓ Webhook created (id: ' + id + ')');
          self.whLog('Response:\n' + JSON.stringify(d, null, 2));
          self.webhookForm.name = '';
          self.webhooks.showCreate = false;
          self.loadWebhooks(true);
        } else {
          const errMsg = res && res.msg ? res.msg : 'Unknown error';
          self.whLog('✗ Error creating webhook: ' + errMsg);
          if (res && res.data) {
            self.whLog('Stacktrace:\n' + JSON.stringify(res.data, null, 2));
          }
        }
      });
    },
    toggleWebhookActive (wh) {
      if (!this.webhooks.channelId) return;
      const newActive = !wh.active;
      this.loading.webhookToggle = true;
      this.webhookLog = '';
      this.whLog((newActive ? 'Enabling' : 'Disabling') + ' webhook "' + wh.name + '" (' + wh.id + ')...');
      const self = this;
      const payload = {
        data: {
          channelId: this.webhooks.channelId,
          webhookId: wh.id,
          webhook: { active: newActive }
        }
      };
      this.doPost(payload, 'superadmin/nezasa-webhooks-update', function (res) {
        self.loading.webhookToggle = false;
        if (res && res.status === 'ok') {
          self.whLog('✓ Webhook "' + wh.name + '" ' + (newActive ? 'enabled' : 'disabled') + '.');
          self.loadWebhooks(true);
        } else {
          const errMsg = res && res.msg ? res.msg : 'Unknown error';
          self.whLog('✗ Error toggling webhook: ' + errMsg);
          if (res && res.data) {
            self.whLog('Stacktrace:\n' + JSON.stringify(res.data, null, 2));
          }
        }
      });
    },
    editWebhook (wh) {
      this.webhooks.showCreate = false;
      this.webhooks.editingWebhook = wh;
      this.webhookEditForm.name = wh.name || '';
      this.webhookEditForm.url = wh.target ? wh.target.url : (wh.url || '');
      this.webhookEditForm.active = !!wh.active;
      this.webhookEditForm.reseller = wh.reseller !== undefined ? !!wh.reseller : true;
      this.webhookEditForm.events = wh.events ? [...wh.events] : [];
      this.webhooks.showEdit = true;
    },
    cancelEditWebhook () {
      this.webhooks.showEdit = false;
      this.webhooks.editingWebhook = null;
    },
    updateWebhook () {
      if (!this.webhooks.channelId || !this.webhooks.editingWebhook) return;
      this.loading.webhookUpdate = true;
      this.webhookLog = '';
      const wh = this.webhooks.editingWebhook;
      this.whLog('Updating webhook "' + wh.name + '" (' + wh.id + ')...');
      const self = this;
      const webhook = {
        name: this.webhookEditForm.name,
        active: this.webhookEditForm.active,
        reseller: this.webhookEditForm.reseller,
        events: this.webhookEditForm.events
      };
      const originalUrl = wh.target ? wh.target.url : (wh.url || '');
      if (this.webhookEditForm.url !== originalUrl) {
        webhook.target = { url: this.webhookEditForm.url };
      }
      const payload = {
        data: {
          channelId: this.webhooks.channelId,
          webhookId: wh.id,
          webhook: webhook
        }
      };
      this.doPost(payload, 'superadmin/nezasa-webhooks-update', function (res) {
        self.loading.webhookUpdate = false;
        if (res && res.status === 'ok') {
          self.whLog('✓ Webhook "' + self.webhookEditForm.name + '" updated.');
          const d = res.data && res.data.data !== undefined ? res.data.data : res.data;
          if (d) {
            self.whLog('Response:\n' + JSON.stringify(d, null, 2));
          }
          self.webhooks.showEdit = false;
          self.webhooks.editingWebhook = null;
          self.loadWebhooks(true);
        } else {
          const errMsg = res && res.msg ? res.msg : 'Unknown error';
          self.whLog('✗ Error updating webhook: ' + errMsg);
          if (res && res.data) {
            self.whLog('Stacktrace:\n' + JSON.stringify(res.data, null, 2));
          }
        }
      });
    },
    deleteWebhook (wh) {
      if (!this.webhooks.channelId) return;
      if (!confirm('Delete webhook "' + wh.name + '"? This cannot be undone.')) return;
      this.loading.webhookDelete = true;
      this.webhookLog = '';
      this.whLog('Deleting webhook "' + wh.name + '" (' + wh.id + ')...');
      const self = this;
      const payload = {
        data: {
          channelId: this.webhooks.channelId,
          webhookId: wh.id
        }
      };
      this.doPost(payload, 'superadmin/nezasa-webhooks-delete', function (res) {
        self.loading.webhookDelete = false;
        if (res && res.status === 'ok') {
          self.whLog('✓ Webhook "' + wh.name + '" deleted.');
          self.loadWebhooks(true);
        } else {
          const errMsg = res && res.msg ? res.msg : 'Unknown error';
          self.whLog('✗ Error deleting webhook: ' + errMsg);
          if (res && res.data) {
            self.whLog('Stacktrace:\n' + JSON.stringify(res.data, null, 2));
          }
        }
      });
    },
    isOurWebhook (wh) {
      const name = (wh.name || '').toLowerCase();
      return name.includes('preview.viavio') || name.includes('live.viavio');
    },
    pingWebhook (wh) {
      if (!this.webhooks.channelId) return;
      this.loading.webhookPing = true;
      this.webhookLog = '';
      var self = this;

      // Step 1: Record the timestamp before sending so we know what "new" means
      var sentAt = Date.now();
      this.whLog('Sending ping to Nezasa for webhook "' + wh.name + '"...');

      var payload = {
        data: {
          channelId: this.webhooks.channelId,
          webhookId: wh.id
        }
      };
      this.doPost(payload, 'superadmin/nezasa-webhooks-ping', function (res) {
        if (res && res.status === 'ok') {
          self.whLog('✓ Ping sent');
          self.whLog('Waiting for ping...');

          // Step 2: Poll the backend to see if a ping was received after sentAt
          var attempts = 0;
          var maxAttempts = 15; // 15 x 2s = 30s timeout
          var pollInterval = setInterval(function () {
            attempts++;
            self.doPost({}, 'superadmin/nezasa-webhooks-last-ping', function (pollRes) {
              var lastPing = null;
              if (pollRes && pollRes.status === 'ok' && pollRes.data) {
                lastPing = pollRes.data.lastPingAt || (pollRes.data.data ? pollRes.data.data.lastPingAt : null);
              }
              if (lastPing && lastPing >= sentAt) {
                clearInterval(pollInterval);
                self.loading.webhookPing = false;
                self.whLog('✓ Ping received! (' + new Date(lastPing).toLocaleTimeString() + ')');
              } else if (attempts >= maxAttempts) {
                clearInterval(pollInterval);
                self.loading.webhookPing = false;
                self.whLog('✗ Ping not received (timed out after 30s)');
              }
            });
          }, 2000);
        } else {
          self.loading.webhookPing = false;
          var errMsg = res && res.msg ? res.msg : 'Unknown error';
          self.whLog('✗ Error sending ping: ' + errMsg);
          if (res && res.data) {
            self.whLog('Stacktrace:\n' + JSON.stringify(res.data, null, 2));
          }
        }
      });
    },

    // ── Log Viewer ──────────────────────────────────────────────────
    selectLogCategory (cat) {
      this.logs.category = cat;
      this.logs.entries = [];
      this.logs.expandedRow = null;
      this.loadLogDates();
    },
    loadLogDates () {
      this.loading.logsDates = true;
      const self = this;
      const payload = { data: { category: this.logs.category } };
      this.doPost(payload, 'superadmin/logs-dates', function (res) {
        self.loading.logsDates = false;
        const d = res && res.data && res.data.data !== undefined ? res.data.data : [];
        self.logs.dates = Array.isArray(d) ? d : [];
        if (self.logs.dates.length) {
          self.logs.selectedDate = self.logs.dates[0];
          self.loadLogEntries();
        } else {
          self.logs.selectedDate = '';
          self.logs.entries = [];
        }
      });
    },
    loadLogEntries () {
      if (!this.logs.selectedDate) return;
      this.loading.logsRead = true;
      this.logs.expandedRow = null;
      this.logs.selectedEntries = [];
      this.logs.selectAll = false;
      const self = this;
      const payload = {
        data: {
          category: this.logs.category,
          date: this.logs.selectedDate,
          search: this.logs.search
        }
      };
      this.doPost(payload, 'superadmin/logs-read', function (res) {
        self.loading.logsRead = false;
        const d = res && res.data && res.data.data !== undefined ? res.data.data : {};
        self.logs.entries = d.entries || [];
        self.logs.raw = d.raw || '';
        self.logs.totalCount = d.totalCount || 0;
        // Auto-scroll console to bottom
        if (self.logs.category === 'console' && self.logs.raw) {
          self.$nextTick(() => {
            const el = self.$refs.consoleBody;
            if (el) el.scrollTop = el.scrollHeight;
          });
        }
      });
    },
    toggleLogRow (idx) {
      if (!this.logs.entries[idx]) return;
      this.logs.expandedRow = this.logs.expandedRow === idx ? null : idx;
    },
    // Everything the backend log parser returns beyond the fields the drawer
    // already renders (e.g. responseBody, status, url, method, checkoutId) —
    // without this, structured error details like Nezasa's rejection reason
    // are in the log file but invisible in the viewer.
    logExtraFields (entry) {
      const KNOWN = ['timestamp', 'level', 'source', 'message', 'stack', 'event', 'nezasa_id', 'worker', 'file'];
      const extra = {};
      Object.keys(entry || {}).forEach((k) => {
        const v = entry[k];
        if (KNOWN.indexOf(k) === -1 && v !== undefined && v !== null && v !== '') extra[k] = v;
      });
      return Object.keys(extra).length ? JSON.stringify(extra, null, 2) : '';
    },
    truncateMessage (msg) {
      if (!msg) return '';
      let str = msg;
      if (typeof str !== 'string') {
        try { str = JSON.stringify(str); } catch (e) { str = String(str); }
      }
      const firstLine = str.split('\n')[0];
      if (firstLine.length <= 120) return firstLine;
      return firstLine.substring(0, 120) + '...';
    },
    ansiToHtml (str) {
      if (!str) return '';
      // Label color mapping
      const LABEL_COLORS = {
        'INFO': { bg: '#3498db', color: '#fff' },
        'AUTH': { bg: '#3498db', color: '#fff' },
        'ERROR': { bg: '#e74c3c', color: '#fff' },
        'DEBUG': { bg: '#9b59b6', color: '#fff' },
        'SERVER': { bg: '#1abc9c', color: '#fff' },
        'LIVE_TEST': { bg: '#f1c40f', color: '#fff' },
        'CRONJOB': { bg: '#2ecc71', color: '#fff' },
        'WEBHOOK': { bg: '#3498db', color: '#fff' }
      };
      // Escape HTML
      let html = str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      // Strip any leftover ANSI codes (from old log files)
      html = html.replace(/\x1B\[[0-9;]*m/g, '');
      // Colorize labels: [TAG] right after timestamp
      html = html.replace(/^(\[[^\]]+\])\s*\[([A-Z_]+)\]/gm, (_, ts, label) => {
        const c = LABEL_COLORS[label] || { bg: '#555', color: '#fff' };
        return '<span style="color:#7f8c8d;">' + ts + '</span> <span style="background:' + c.bg + ';color:' + c.color + ';padding:1px 5px;border-radius:3px;font-weight:bold;">[' + label + ']</span>';
      });
      // Colorize file references: [file => line]
      html = html.replace(/\[([^\]]+?\s+=&gt;\s+\d+)\]/g, '<span style="color:#FF7518;">[$1]</span>');
      return html;
    },
    highlightSearch (text) {
      if (!this.logs.search || !text) return text;
      const escaped = this.logs.search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const re = new RegExp('(' + escaped + ')', 'gi');
      return text.replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(re, '<mark>$1</mark>');
    },
    toggleSelectAllLogs () {
      if (this.logs.selectAll) {
        this.logs.selectedEntries = this.logs.entries.map((_, i) => i);
      } else {
        this.logs.selectedEntries = [];
      }
    },
    exportSelectedLogs () {
      if (!this.logs.selectedEntries.length) return;
      const exported = this.logs.selectedEntries
        .slice()
        .sort((a, b) => a - b)
        .map(idx => {
          const e = this.logs.entries[idx];
          const obj = {
            timestamp: e.timestamp,
            level: e.level,
            source: e.source,
            message: e.message
          };
          if (e.stack) obj.stack = e.stack.split('\n').filter(l => l.trim());
          return obj;
        });
      const json = JSON.stringify(exported, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'logs_' + (this.logs.category || 'export') + '_' + (this.logs.selectedDate || 'all') + '.json';
      a.click();
      URL.revokeObjectURL(url);
    },

    // ── Database Operations ─────────────────────────────────────────
    loadDbOpsSettings () {
      const self = this;
      this.doPost({}, 'settings/get', function (res) {
        if (res && res.data && res.data.data) {
          const s = res.data.data;
          if (s.auto_handling) {
            self.autoMode = s.auto_handling;
          }
          if (s.auto_period) {
            self.autoPeriod = s.auto_period;
          }
          self.prevAutoMode = self.autoMode;
          self.dbOpsLoaded = true;
        }
      });
    },
    getTodayDate () {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    formatDateForDisplay (dateStr) {
      if (!dateStr) return '';
      // Handle ISO strings like "2025-02-01T00:00:00.000Z"
      const str = String(dateStr).slice(0, 10);
      const parts = str.split('-');
      if (parts.length === 3) {
        return `${parts[2]}.${parts[1]}.${parts[0]}`;
      }
      return dateStr;
    },
    async archiveTrips () {
      if (!this.archiveDate) return;
      this.archiving = true;
      const self = this;
      await this.doPost({ data: { date: this.archiveDate } }, 'settings/archive-trips', function (res) {
        if (res.data.status === 'error') {
          self.help.log(self.$t(res.msg), false, true);
        } else {
          self.options.showSuccess = true;
        }
      });
      this.archiving = false;
    },
    async deleteTrips () {
      if (!this.deleteDate) return;
      this.deleting = true;
      const self = this;
      await this.doPost({ data: { date: this.deleteDate } }, 'settings/delete-trips', function (res) {
        if (res.data.status === 'error') {
          self.help.log(self.$t(res.msg), false, true);
        } else {
          self.options.showSuccess = true;
        }
      });
      this.deleting = false;
    },
    async saveAutoSettings () {
      const payload = {
        autoHandling: this.autoMode || null,
        autoPeriod: this.autoPeriod || null
      };
      const self = this;
      await this.doPost({ data: payload }, 'settings/save', function (res) {
        if (res.data.status === 'error') {
          self.help.log(self.$t(res.msg), false, true);
        } else {
          self.options.showSuccess = true;
        }
      });
    },
    async testHardDelete () {
      if (!this.autoPeriod) return;
      this.testDeleteLoading = true;
      const self = this;
      await this.doPost({ data: { period: this.autoPeriod } }, 'settings/preview-hard-delete', function (res) {
        self.testDeleteLoading = false;
        if (res && res.data && res.data.data) {
          const d = res.data.data;
          self.testDeleteDialog.cutoffDate = d.cutoffDate || '';
          self.testDeleteDialog.trips = d.trips || [];
          self.testDeleteDialog.visible = true;
        }
      });
    },
    async executeTestHardDelete () {
      if (!this.testDeleteDialog.cutoffDate) return;
      this.testDeleteExecuting = true;
      const self = this;
      await this.doPost({ data: { date: this.testDeleteDialog.cutoffDate } }, 'settings/execute-hard-delete', function (res) {
        self.testDeleteExecuting = false;
        if (res && res.data && res.data.status !== 'error') {
          self.options.showSuccess = true;
          self.testDeleteDialog.visible = false;
          self.testDeleteDialog.trips = [];
        }
      });
    },
    confirmDeleteTripById () {
      if (!this.deleteTripId) return;
      const L = this.options.Lang;
      const tripId = this.deleteTripId.trim();
      Object.assign(this.options.globalConfirm, {
        visible: true,
        title: L.DeleteTripById || 'Delete Trip by ID',
        message: (L.ConfirmDeleteTripById || 'Are you sure you want to permanently delete this trip?') + '\n\nTrip ID: ' + tripId + '\n\n' + (L.DeleteTripByIdWarning || 'This will permanently remove the trip, all its translations, associated PDFs and JSON files. This action cannot be undone.'),
        confirmBtnText: L.DeletePermanently || 'Delete permanently',
        confirmBtnClass: 'md-raised md-accent',
        onConfirm: async () => {
          this.options.globalConfirm.visible = false;
          await this.hardDeleteTripById();
        },
        onCancel: () => {
          this.options.globalConfirm.visible = false;
        }
      });
    },
    async hardDeleteTripById () {
      const tripId = this.deleteTripId.trim();
      if (!tripId) return;
      this.deleteTripIdLoading = true;
      const self = this;
      await this.doPost({ data: { tripId } }, 'settings/hard-delete-trip-by-id', function (res) {
        self.deleteTripIdLoading = false;
        if (res && res.data && res.data.status !== 'error') {
          self.options.showSuccess = true;
          self.deleteTripId = '';
        } else {
          const msg = res && res.data && res.data.msg ? res.data.msg : 'Trip not found or could not be deleted';
          self.help.log(msg, false, true);
        }
      });
    },
    async openDbOpsConfirm (action) {
      const dateStr = (action.includes('archive') ? this.archiveDate : this.deleteDate) || '';
      const payload = {
        visible: true,
        title: '',
        message: '',
        confirmBtnText: 'Confirm',
        confirmBtnClass: 'md-raised md-primary',
        onConfirm: null,
        onCancel: null
      };

      let count = 0;
      if (action === 'archive-now' && this.archiveDate) {
        const self = this;
        await self.doPost({ data: { date: this.archiveDate } }, 'settings/count-archive-trips', function (res) {
          if (res.data.status !== 'error' && res.data.data && typeof res.data.data.count !== 'undefined') {
            count = res.data.data.count;
          }
        });
      } else if (action === 'delete-now' && this.deleteDate) {
        const self = this;
        await self.doPost({ data: { date: this.deleteDate } }, 'settings/count-delete-trips', function (res) {
          if (res.data.status !== 'error' && res.data.data && typeof res.data.data.count !== 'undefined') {
            count = res.data.data.count;
          }
        });
      }

      const L = this.options.Lang;
      if (action === 'archive-now') {
        payload.title = L.ArchiveTrips || 'Archive trips';
        payload.message = (L.ConfirmArchiveNow || 'Are you sure you want to archive all trips up to this date?') + '\n' + this.formatDateForDisplay(dateStr) + ' (' + count + ' ' + (L.TripsWillBeAffected || 'trip(s) will be affected') + ')';
        payload.confirmBtnText = L.ArchiveNow || 'Archive now';
        payload.confirmBtnClass = 'md-raised md-accent';
        payload.onConfirm = async () => { this.options.globalConfirm.visible = false; await this.archiveTrips(); };
      } else if (action === 'delete-now') {
        payload.title = L.DeleteTrips || 'Delete trips';
        payload.message = (L.ConfirmDeleteNow || 'Are you sure you want to permanently delete all trips up to this date?') + '\n' + this.formatDateForDisplay(dateStr) + ' (' + count + ' ' + (L.TripsWillBeAffected || 'trip(s) will be affected') + ')';
        payload.confirmBtnText = L.DeleteNow || 'Delete now';
        payload.confirmBtnClass = 'md-raised md-accent';
        payload.onConfirm = async () => { this.options.globalConfirm.visible = false; await this.deleteTrips(); };
      }
      payload.onCancel = () => {
        this.options.globalConfirm.visible = false;
      };
      Object.assign(this.options.globalConfirm, payload);
    },
    onAutoModeChange () {
      const val = this.autoMode;
      if (val === 'delete' && this.prevAutoMode !== 'delete' && this.dbOpsLoaded) {
        const L = this.options.Lang;
        const msg = L.ConfirmAutoDeleteMode || 'Are you sure you want to enable automated deletion of old trips? Deleted trips are permanently removed and cannot be retrieved.';
        Object.assign(this.options.globalConfirm, {
          visible: true,
          title: L.AutomatedDeleteTrips || 'Auto delete',
          message: msg,
          confirmBtnText: L.Confirm || 'Confirm',
          confirmBtnClass: 'md-raised md-accent',
          onConfirm: () => {
            this.options.globalConfirm.visible = false;
            this.prevAutoMode = val;
          },
          onCancel: () => {
            this.options.globalConfirm.visible = false;
            this.autoMode = this.prevAutoMode;
          }
        });
        return;
      }
      this.prevAutoMode = val;
    },

    // ── Files Tab Methods ─────────────────────────────────────────
    scanOrphanedFiles () {
      this.orphanedFiles.loading = true;
      this.orphanedFiles.selected = [];
      this.orphanedFiles.selectAll = false;
      this.doPost({}, 'settings/scan-orphaned-files', (res) => {
        this.orphanedFiles.loading = false;
        this.orphanedFiles.scanned = true;
        if (res && res.data && res.data.data) {
          const d = res.data.data;
          this.orphanedFiles.items = d.items || [];
          this.orphanedFiles.totalFiles = d.totalFiles || 0;
          this.orphanedFiles.totalSize = d.totalSize || 0;
          this.orphanedFiles.orphanedFileCount = d.orphanedFiles || 0;
          this.orphanedFiles.orphanedSize = d.orphanedSize || 0;
          this.orphanedFiles.dirKeys = d.dirKeys || [];
        }
        this.resizeOrphanedTable();
      });
    },
    toggleSelectAllOrphans () {
      if (this.orphanedFiles.selectAll) {
        this.orphanedFiles.selected = this.filteredOrphanedRows.map(r => r.file);
      } else {
        this.orphanedFiles.selected = [];
      }
    },
    deleteSelectedOrphans () {
      const L = this.options.Lang;
      const count = this.orphanedFiles.selected.length;
      Object.assign(this.options.globalConfirm, {
        visible: true,
        hideCancel: false,
        title: L.DeleteSelectedFiles || 'Delete Selected Files',
        message: (L.ConfirmDeleteSelectedFiles || 'Are you sure you want to permanently delete') + ' ' + count + ' ' + (L.FilesQuestionMark || 'file(s)?') + ' ' + (L.ActionCannotBeUndone || 'This action cannot be undone.'),
        confirmBtnText: (L.löschen || 'Delete') + ' ' + count + ' ' + (L.Dateien || 'file(s)'),
        confirmBtnClass: 'md-raised md-accent',
        onConfirm: () => {
          this.options.globalConfirm.visible = false;
          this._executeDeleteFiles(this.orphanedFiles.selected);
        },
        onCancel: () => { this.options.globalConfirm.visible = false; }
      });
    },
    deleteAllOrphans () {
      const L = this.options.Lang;
      const orphaned = this.allOrphanedFiles;
      if (orphaned.length === 0) return;
      const totalSize = orphaned.reduce((sum, o) => sum + (o.file.sizeBytes || 0), 0);
      Object.assign(this.options.globalConfirm, {
        visible: true,
        hideCancel: false,
        title: L.DeleteAllOrphanedFiles || 'Delete All Orphaned Files',
        message: (L.ConfirmDeleteAllOrphaned || 'Permanently delete ALL') + ' ' + orphaned.length + ' ' + (L.OrphanedFilesLower || 'orphaned file(s)') + ' (' + this.formatFileSize(totalSize) + ')?\n\n' + (L.OrphanedNoReferenceWarning || 'These files have no reference in any database table.') + ' ' + (L.ActionCannotBeUndone || 'This action cannot be undone.'),
        confirmBtnText: (L.DeleteAllOrphaned || 'Delete all') + ' ' + orphaned.length + ' ' + (L.OrphanedFilesLower || 'orphaned'),
        confirmBtnClass: 'md-raised md-accent',
        onConfirm: () => {
          this.options.globalConfirm.visible = false;
          this._executeDeleteFiles(orphaned.map(o => o.file), orphaned);
        },
        onCancel: () => { this.options.globalConfirm.visible = false; }
      });
    },
    _executeDeleteFiles (selectedFiles, orphanedWithTripId) {
      const L = this.options.Lang;
      this.orphanedFiles.deleting = true;
      const files = selectedFiles.map(f => ({
        dirKey: f.dirKey,
        filename: f.filename,
        subDir: f.subDir || undefined,
        tripId: undefined
      }));
      if (orphanedWithTripId) {
        for (const o of orphanedWithTripId) {
          const match = files.find(f => f.dirKey === o.file.dirKey && f.filename === o.file.filename);
          if (match) match.tripId = o.tripId;
        }
      } else {
        for (const item of this.orphanedFiles.items) {
          for (const file of item.files) {
            const match = files.find(f => f.dirKey === file.dirKey && f.filename === file.filename);
            if (match) match.tripId = item.tripId;
          }
        }
      }
      this.doPost({ data: { files } }, 'settings/delete-orphaned-files', (res) => {
        this.orphanedFiles.deleting = false;
        if (res && res.data && res.data.data) {
          const d = res.data.data;
          const errMsg = d.errors && d.errors.length ? '\n\n' + (L.Errors || 'Errors') + ': ' + d.errors.length + ' ' + (L.FilesCouldNotBeDeleted || 'file(s) could not be deleted.') : '';
          Object.assign(this.options.globalConfirm, {
            visible: true,
            hideCancel: true,
            title: L.FilesDeleted || 'Files Deleted',
            message: (L.SuccessfullyDeleted || 'Successfully deleted') + ' ' + (d.deleted || 0) + ' ' + (L.Dateien || 'file(s)') + '.' + errMsg,
            confirmBtnText: 'OK',
            confirmBtnClass: 'md-raised md-primary',
            onConfirm: () => { this.options.globalConfirm.visible = false; },
            onCancel: () => { this.options.globalConfirm.visible = false; }
          });
        }
        this.orphanedFiles.scanned = false;
        this.scanOrphanedFiles();
      });
    },
    formatFileSize (bytes) {
      if (!bytes || bytes === 0) return '0 B';
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    },
    resizeOrphanedTable () {
      this.$nextTick(() => {
        const el = this.$refs.orphanedTableScroll;
        if (!el) return;
        const top = el.getBoundingClientRect().top;
        // 60px bottom padding for delete button + breathing room
        el.style.maxHeight = (window.innerHeight - top - 60) + 'px';
      });
    },

    // ── Secrets Vault Methods ────────────────────────────────────
    openAddSecretModal () {
      const m = this.options.secretsModal;
      m.mode = 'secret';
      m.key_name = '';
      m.value = '';
      m.category = 'general';
      m.description = '';
      m.showValue = false;
      m.setName = '';
      m.setUsername = '';
      m.setPassword = '';
      m.showPassword = false;
      m.saving = false;
      const self = this;
      m.onSave = () => {
        if (m.category === 'nezasa_sets') {
          self.addNezasaSet();
        } else {
          self.addNewSecret();
        }
      };
      m.onClose = () => { m.visible = false; };
      m.visible = true;
    },

    openNezasaEnableModal () {
      const m = this.options.secretsModal;
      m.mode = 'nezasa_enable';
      m.nezasaSets = [];
      m.activeNezasaSet = '';
      m.saving = false;
      const self = this;
      m.onSave = (setName) => { self.onNezasaActiveSetChange(setName); };
      m.onClose = () => { m.visible = false; };
      m.visible = true;
      this.loadNezasaSets();
    },

    checkSecrets2FA () {
      this.loading.secrets2fa = true;
      const self = this;
      this.doPost({}, 'superadmin/secrets-check-2fa', function (res) {
        self.loading.secrets2fa = false;
        const d = res && res.data && res.data.data ? res.data.data : {};
        self.secrets.twoFaEnabled = !!d.twoFaEnabled;
        self.secrets.debugBypass = !!d.debugBypass;
        self.secrets.twoFaChecked = true;
        // DEBUG mode bypasses 2FA: treat as verified and load the vault directly.
        if (self.secrets.debugBypass) {
          self.secrets.twoFaVerified = true;
          self.loadSecretsList();
          self.loadNezasaSets();
        }
      });
    },

    verifySecrets2FA () {
      if (!this.secrets.totpCode) return;
      this.loading.secrets2fa = true;
      this.secrets.totpError = '';
      const self = this;
      const payload = {
        data: {
          token: this.secrets.totpCode
        }
      };
      this.doPost(payload, 'totp-verify-secrets', function (res) {
        self.loading.secrets2fa = false;
        if (res && res.status === 'ok') {
          self.secrets.twoFaVerified = true;
          self.secrets.totpCode = '';
          self.loadSecretsList();
          self.loadNezasaSets();
        } else {
          self.secrets.totpError = (res && res.msg) || 'Invalid code. Please try again.';
        }
      });
    },

    loadSecretsList () {
      this.loading.secrets = true;
      const self = this;
      this.doPost({}, 'superadmin/secrets-list', function (res) {
        self.loading.secrets = false;
        if (res && res.data && res.data.data) {
          self.secrets.list = res.data.data;
        }
        self.secrets.loaded = true;
      });
    },

    revealSecret (keyName) {
      const self = this;
      // Toggle off if already revealed
      if (this.secrets.revealedValues[keyName]) {
        self.$set(self.secrets.revealedValues, keyName, null);
        return;
      }
      this.loading.secretReveal = true;
      const payload = { data: { key_name: keyName } };
      this.doPost(payload, 'superadmin/secrets-get-value', function (res) {
        self.loading.secretReveal = false;
        if (res && res.data && res.data.data) {
          self.$set(self.secrets.revealedValues, keyName, res.data.data.value);
        }
      });
    },

    startEditSecret (keyName, setName) {
      this.secrets.editingKey = keyName;
      this.secrets.editValue = '';
      this.secrets.showEditValue = false;
      // Pre-fill with revealed value if available
      if (this.secrets.revealedValues[keyName]) {
        this.secrets.editValue = this.secrets.revealedValues[keyName];
      }
    },

    cancelEdit () {
      this.secrets.editingKey = null;
      this.secrets.editValue = '';
      this.secrets.showEditValue = false;
    },

    saveEditedSecret (keyName) {
      this.loading.secretSave = true;
      const self = this;
      // Find existing secret to preserve category/description
      const existing = this.secrets.list.find(s => s.key_name === keyName) || {};
      const payload = {
        data: {
          key_name: keyName,
          value: this.secrets.editValue,
          category: existing.category || 'general',
          description: existing.description || ''
        }
      };
      this.doPost(payload, 'superadmin/secrets-save', function (res) {
        self.loading.secretSave = false;
        self.cancelEdit();
        // Clear revealed value
        self.$set(self.secrets.revealedValues, keyName, null);
        self.loadSecretsList();
      });
    },

    clearSecret (keyName, category, description) {
      this.loading.secretSave = true;
      const self = this;
      const payload = {
        data: {
          key_name: keyName,
          value: '',
          category: category || 'general',
          description: description || ''
        }
      };
      this.doPost(payload, 'superadmin/secrets-save', function (res) {
        self.loading.secretSave = false;
        self.$set(self.secrets.revealedValues, keyName, null);
        self.loadSecretsList();
      });
    },

    deleteSecretConfirm (keyName) {
      if (!confirm('Are you sure you want to delete "' + keyName + '"? This cannot be undone.')) return;
      this.loading.secretDelete = true;
      const self = this;
      const payload = { data: { key_name: keyName } };
      this.doPost(payload, 'superadmin/secrets-delete', function (res) {
        self.loading.secretDelete = false;
        self.$set(self.secrets.revealedValues, keyName, null);
        self.loadSecretsList();
      });
    },

    addNewSecret () {
      const m = this.options.secretsModal;
      if (!m.key_name || !m.value) return;
      m.saving = true;
      const self = this;
      const payload = {
        data: {
          key_name: m.key_name.toUpperCase().replace(/[^A-Z0-9_]/g, '_'),
          value: m.value,
          category: m.category,
          description: m.description
        }
      };
      this.doPost(payload, 'superadmin/secrets-save', function (res) {
        m.saving = false;
        m.visible = false;
        self.loadSecretsList();
      });
    },

    loadNezasaSets () {
      this.loading.nezasaSets = true;
      const self = this;
      this.doPost({}, 'superadmin/secrets-nezasa-sets', function (res) {
        self.loading.nezasaSets = false;
        if (res && res.data && res.data.data) {
          self.nezasaSets.activeSet = res.data.data.activeSet || '';
          self.nezasaSets.sets = res.data.data.sets || [];
          // Also update modal data for the enable modal
          const m = self.options.secretsModal;
          m.nezasaSets = res.data.data.sets || [];
          m.activeNezasaSet = res.data.data.activeSet || '';
        }
      });
    },

    onNezasaActiveSetChange (val) {
      const self = this;
      const payload = { data: { set_name: val } };
      this.doPost(payload, 'superadmin/secrets-nezasa-active', function (res) {
        self.loadNezasaSets();
        if (res && res.data && res.data.restart_required) {
          self.showSuccessMsg('Nezasa Set geändert — Backend wird neu gestartet...');
        }
      });
    },

    async addNezasaSet () {
      const m = this.options.secretsModal;
      if (!m.setName || !m.setUsername) return;
      m.saving = true;
      const self = this;

      const keysToSave = [
        { key: 'NEZASA_USER__' + m.setName, value: m.setUsername },
        { key: 'NEZASA_PW__' + m.setName, value: m.setPassword }
      ];

      for (const entry of keysToSave) {
        if (!entry.value) continue;
        await new Promise(resolve => {
          self.doPost({
            data: {
              key_name: entry.key,
              value: entry.value,
              category: 'nezasa_sets',
              description: 'Nezasa set: ' + m.setName
            }
          }, 'superadmin/secrets-save', function () {
            resolve();
          });
        });
      }

      m.saving = false;
      m.visible = false;
      this.loadNezasaSets();
      this.loadSecretsList();
    },

    deleteNezasaSet (setName) {
      if (!confirm('Delete the entire Nezasa set "' + setName + '"? This cannot be undone.')) return;
      this.loading.nezasaSets = true;
      const self = this;
      // Delete all keys matching __setName
      const keysToDelete = this.secrets.list
        .filter(s => s.key_name.endsWith('__' + setName))
        .map(s => s.key_name);

      let deleted = 0;
      const total = keysToDelete.length;
      keysToDelete.forEach(keyName => {
        self.doPost({ data: { key_name: keyName } }, 'superadmin/secrets-delete', function () {
          deleted++;
          if (deleted >= total) {
            self.loadNezasaSets();
            self.loadSecretsList();
          }
        });
      });
      if (total === 0) {
        this.loading.nezasaSets = false;
      }
    }
  }
}
</script>

<style scoped>
.super-admin {
  padding: 20px;
}
.ndbg-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}
.ndbg-modal {
  background: #fff;
  width: min(440px, 92vw);
  border-radius: 8px;
  padding: 24px;
}
.orphaned-files-tab {
  overflow: hidden;
}
.files-card-header {
  padding: 20px 16px !important;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.orphaned-files-table-scroll {
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
}
.orphaned-row {
  background: #fff5f5;
}
.orphaned-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  background: #ffebee;
  color: #d32f2f;
}
.linked-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  background: #e8f5e9;
  color: #2e7d32;
}
.type-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  background: #e3f2fd;
  color: #1565c0;
  text-transform: capitalize;
}

.tab-buttons {
  display: flex;
  border-bottom: 1px #E1E8F3 solid;
  justify-content: center;
  margin-bottom: 20px;
  gap: 0;
}

.tab-btn {
  padding: 14px 28px;
  font-size: 17px;
  background: #F5F6FA;
  border: none;
  cursor: pointer;
  color: #444;
  transition: 0.2s;
  font-family: 'Nunito Sans', sans-serif !important;
  letter-spacing: -0.08px;
}

.tab-btn:hover {
  background: #eef0f5;
}

.tab-btn.active {
  background: #E1E8F3;
  border-color: #ccc;
}

.tab-content {
  padding: 16px 0;
}

.mb-20 {
  margin-bottom: 20px;
}

.mb-10 {
  margin-bottom: 10px;
}

.range-preview {
  margin-top: 10px;
}

.backfill-result {
  padding: 10px;
  background: #e8f5e9;
  border-radius: 4px;
  font-size: 14px;
}

.mt-10 {
  margin-top: 10px;
}

.debug-output {
  background: #111;
  color: #0f0;
  padding: 10px;
  border-radius: 4px;
  max-height: 300px;
  overflow: auto;
  font-size: 12px;
}

.heartbeat-saved {
  color: #4caf50;
  font-weight: bold;
  font-size: 14px;
}

.heartbeat-status {
  padding: 12px;
  background: #f5f5f5;
  border-radius: 4px;
}

.heartbeat-info div {
  margin-bottom: 4px;
  font-size: 14px;
}

.heartbeat-countdown {
  font-family: monospace;
  font-size: 16px;
  font-weight: bold;
  color: #1565c0;
}

.status-ok {
  color: #4caf50;
}

.status-failed {
  color: #f44336;
}

/* Cron job status bar */
.cron-status-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
  padding: 10px 14px;
  background: #e8f5e9;
  border: 1px solid #a5d6a7;
  border-radius: 6px;
}

.cron-status-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #2e7d32;
}

.cron-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
}

.cron-dot-active {
  background: #4caf50;
  box-shadow: 0 0 6px rgba(76, 175, 80, 0.6);
  animation: cron-pulse 2s ease-in-out infinite;
}

@keyframes cron-pulse {
  0%, 100% { box-shadow: 0 0 4px rgba(76, 175, 80, 0.4); }
  50% { box-shadow: 0 0 10px rgba(76, 175, 80, 0.8); }
}

.cron-label {
  white-space: nowrap;
}

.cron-next-run {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: #333;
}

.cron-countdown {
  margin-left: 6px;
  font-family: monospace;
  font-size: 13px;
  font-weight: bold;
  color: #1565c0;
}

/* Nezasa Webhooks */
.wh-badge-active {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: bold;
  background: #4caf50;
  color: #fff;
}

.wh-badge-inactive {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: bold;
  background: #f44336;
  color: #fff;
}

.wh-url {
  font-family: monospace;
  font-size: 12px;
  word-break: break-all;
}

.wh-create-form {
  padding: 16px;
  background: #f5f5f5;
  border-radius: 4px;
}

.wh-events-section {
  margin-top: 8px;
}

.wh-events-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
  margin-top: 6px;
}

.wh-event-checkbox {
  font-size: 13px;
  font-family: monospace;
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  min-width: 220px;
}

.wh-event-code {
  font-family: monospace;
  font-size: 13px;
  background: #e3f2fd;
  padding: 2px 6px;
  border-radius: 3px;
  color: #1565c0;
}

.wh-state-badge {
  font-family: monospace;
  font-size: 12px;
  background: #e8f5e9;
  padding: 2px 8px;
  border-radius: 10px;
  color: #2e7d32;
  font-weight: bold;
}

.wh-missing-secret {
  display: flex;
  align-items: center;
  padding: 14px 18px;
  background: #fff3e0;
  border: 1px solid #ff9800;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  color: #e65100;
}

.wh-missing-secret code {
  background: #fbe9e7;
  padding: 2px 6px;
  border-radius: 3px;
  font-family: monospace;
  font-weight: bold;
}

.wh-console {
  min-height: 80px;
  max-height: 400px;
  white-space: pre-wrap;
  word-break: break-all;
}

/* Log Viewer */
.logs-controls {
  display: flex;
  flex-direction: column;
  gap: 12px;
  text-align: left;
}

.logs-categories {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.logs-filters {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.logs-date-select {
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  min-width: 160px;
  background: #fff;
}

.logs-search-input {
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  min-width: 200px;
  outline: none;
}

.logs-search-input:focus {
  border-color: #448aff;
}

.logs-stats {
  font-size: 13px;
  color: #666;
  padding: 6px 10px;
  background: #f5f5f5;
  border-radius: 4px;
  display: inline-block;
}

.log-row-clickable {
  cursor: pointer;
}

.log-row-clickable:hover {
  background: #f5f5f5;
}

.log-level-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: bold;
  color: #fff;
  text-transform: uppercase;
}

.log-level-error {
  background: #e74c3c;
}

.log-level-info {
  background: #3498db;
}

.log-level-auth {
  background: #e67e22;
}

.log-level-webhook {
  background: #2ecc71;
}

.log-message-cell {
  max-width: 400px;
}

.log-message-truncated {
  display: inline;
  font-family: monospace;
  font-size: 12px;
  word-break: break-all;
}

.log-stack-row {
  background: #f9f9f9;
}

.log-stack-pre {
  white-space: pre-wrap;
  word-wrap: break-word;
  max-height: 400px;
  margin: 0;
}

.log-detail-drawer {
  padding: 8px 4px;
}

.log-detail-section {
  margin-bottom: 12px;
}

.log-detail-section:last-child {
  margin-bottom: 0;
}

.log-detail-label {
  font-size: 11px;
  font-weight: bold;
  text-transform: uppercase;
  color: #888;
  margin-bottom: 4px;
  letter-spacing: 0.5px;
}

.log-detail-meta {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.log-meta-tag {
  font-size: 12px;
  color: #555;
  background: #eef2f7;
  padding: 2px 8px;
  border-radius: 4px;
  font-family: monospace;
}

.log-meta-source {
  background: #fff3cd;
  color: #856404;
  font-weight: bold;
}

.super-admin >>> .md-table-head-label,
.super-admin >>> .md-table-cell-container {
  text-align: left !important;
}

.logs-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
}

.log-checkbox {
  margin: 0 !important;
}

.super-admin >>> .log-checkbox .md-checkbox-container {
  margin: 0;
}

/* Hard Delete Preview Overlay — matches #globalConfirmOverlay / #globalConfirmModal */
.hd-overlay {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  z-index: 100000000001 !important;
  background: rgba(0,0,0,0.45);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
}

.hd-modal {
  background: #fff;
  padding: 2rem;
  width: 55rem;
  max-width: 90vw;
  max-height: 85vh;
  overflow: auto;
  border-radius: 12px;
}

.hd-title {
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 4px 0;
  text-align: center;
}

.hd-subtitle {
  font-size: 14px;
  color: #666;
  margin: 0 0 20px 0;
  text-align: center;
}

.hd-warning {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: #fff3e0;
  border: 1px solid #ffb74d;
  border-radius: 6px;
  font-size: 13px;
  color: #e65100;
  margin-bottom: 20px;
}

.hd-warning .md-icon {
  font-size: 20px !important;
  color: #ff9800;
}

.hd-stats {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
}

.hd-stat {
  flex: 1;
  background: #f5f6fa;
  border-radius: 6px;
  padding: 12px 16px;
  text-align: center;
}

.hd-stat-num {
  display: block;
  font-size: 22px;
  font-weight: 700;
  color: #333;
  line-height: 1.2;
}

.hd-stat-label {
  display: block;
  font-size: 11px;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 2px;
}

.hd-table-wrap {
  max-height: 340px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  margin-bottom: 20px;
}

.hd-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.hd-table thead {
  position: sticky;
  top: 0;
  z-index: 1;
}

.hd-table th {
  background: #f5f6fa;
  padding: 10px 12px;
  text-align: left;
  font-weight: 600;
  color: #555;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  border-bottom: 2px solid #e0e0e0;
}

.hd-table td {
  padding: 8px 12px;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: top;
}

.hd-table tbody tr:hover {
  background: #fafbfd;
}

.hd-table tbody tr:last-child td {
  border-bottom: none;
}

.hd-cell-id {
  font-family: monospace;
  font-weight: 600;
  font-size: 13px;
  color: #1565c0;
  white-space: nowrap;
}

.hd-cell-date {
  white-space: nowrap;
  color: #555;
}

.hd-file-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 11px;
  font-family: monospace;
  margin: 1px 2px;
  word-break: break-all;
}

.hd-file-json {
  background: #e3f2fd;
  color: #1565c0;
}

.hd-file-pdf {
  background: #fce4ec;
  color: #c62828;
}

.hd-file-upload {
  background: #f3e5f5;
  color: #6a1b9a;
}

.hd-none {
  color: #ccc;
  font-size: 12px;
}

.hd-empty {
  padding: 40px 20px;
  text-align: center;
  color: #999;
  font-size: 14px;
  margin-bottom: 20px;
}

.hd-buttons {
  display: flex;
  justify-content: center;
  gap: 1rem;
  padding-top: 20px;
  border-top: 1px solid #e9e9e9;
}

/* Console terminal view */
.console-terminal {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.console-terminal-header {
  background: #3a3a3a;
  padding: 8px 12px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.console-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  display: inline-block;
}

.console-dot-red { background: #ff5f57; }
.console-dot-yellow { background: #ffbd2e; }
.console-dot-green { background: #28c840; }

.console-terminal-title {
  color: #999;
  font-size: 12px;
  margin-left: 8px;
  font-family: monospace;
}

.console-terminal-body {
  background: #1e1e1e;
  color: #d4d4d4;
  font-family: 'Courier New', Consolas, 'Liberation Mono', monospace;
  font-size: 12px;
  line-height: 1.5;
  padding: 16px;
  margin: 0;
  overflow: auto;
  max-height: 75vh;
  white-space: pre;
  tab-size: 4;
}

/* Secrets Vault */
.secrets-toolbar {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 20px;
}

.secrets-toolbar-left {
  width: 220px;
}

.secrets-toolbar-right {
  display: flex;
  gap: 8px;
  align-items: center;
}

.secrets-2fa-gate {
  max-width: 500px;
  margin: 40px auto;
}

.secret-masked {
  color: #999;
  letter-spacing: 1px;
  word-break: break-all;
}

.secret-revealed {
  color: #333;
  font-weight: 500;
  word-break: break-all;
}

.secret-edit-row {
  display: flex;
  align-items: center;
  gap: 4px;
}

.secret-field-row {
  margin-bottom: 12px;
}

.secret-label {
  font-size: 12px;
  color: #666;
  font-family: monospace;
  display: block;
  margin-bottom: 2px;
}

.secret-value-row {
  display: flex;
  align-items: center;
  gap: 2px;
}

.secret-category-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: bold;
  text-transform: uppercase;
}

.cat-auth { background: #e8eaf6; color: #283593; }
.cat-mail { background: #e0f7fa; color: #006064; }
.cat-nezasa { background: #fff3e0; color: #e65100; }
.cat-nezasa_sets { background: #fbe9e7; color: #bf360c; }
.cat-external { background: #f3e5f5; color: #6a1b9a; }
.cat-general { background: #f5f5f5; color: #616161; }
</style>

